// IE Hub - Screen Renderer
// Main component for rendering JSON-driven screens
// Render Ieform and Budget Breakdown screens

import {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  Fragment,
} from "react";
import { useNavigate } from "react-router-dom";
import { useWatch } from "react-hook-form";
import {
  alpha,
  Box,
  UIButton as Button,
  Container,
  Grid,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Link,
  IconButton,
  Tooltip,
} from "../../ui";
import { FormHelperText } from "@mui/material";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FormProvider, useForm, Controller } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextField from "../../ui/fields/TextField";
import SpecialTextField from "../../ui/fields/SpecialTextField";
import Select from "../../ui/fields/Select";
import Checkbox from "../../ui/fields/Checkbox";
import CheckboxGroup from "../../ui/fields/CheckboxGroup";
import RadioGroup from "../../ui/fields/RadioGroup";
import DateField from "../../ui/fields/DateField";
import ToggleButton from "../../ui/fields/ToggleButton";
import Counter from "../../ui/fields/Counter";
import DateWithClear from "../../ui/fields/DateWithClear";
import CountLabel from "../../ui/fields/CountLabel";
// Visibility evaluation is handled by parseVisibilityCondition helper
import {
  evaluateNode,
  evaluateVisibilityRule,
  VisibilityNode,
} from "../runtime/visibility/dsl";
import { runEffects } from "../actions/effects";
import InfoDrawer from "../../components/InfoDrawer";
import { infoContentSchema } from "../../shared/types/infoContent";
import privacyJson from "../../knowledge/privacy.json";
import { calculateAge } from "@/shared/lib/utils/others";
import { UI_MESSAGES } from "@/constants/ui";

// Import custom components for budget screen
import QuestionGroup from "../../components/QuestionGroup";
import TextWithTooltip from "../../components/TextWithTooltip";
import SelectToggle from "../../components/SelectToggle";
import CollapsibleTextArea from "../../components/CollapsibleTextArea";
import SectionHelperText from "../../components/SectionHelperText";
import DebtCategory from "../../components/DebtCategory";
import JsonHelpDialog from "../../components/JsonHelpDialog";
import NotesDialog from "../../components/NotesDialog";
import ListForm from "../../components/ListForm";
import { openConfirmDialog } from "../../components/ConfirmDialog";
import type { OpenActionSpec } from "@/shared/warnings/types";
import type { DebtCategoryKey, DebtFormEntry } from "@/shared/api/types";
import type {
  AccountTypeRecord,
  LenderParentRecord,
} from "@/shared/api/services/lenderService";
import ArrearForm from "@/components/ArrearForm";
import UuViewer from "../../components/uu/uuEngine/uuViwer";
import { useUuFieldController } from "../../components/uu/hooks/useUuFieldController";
import { isUuFieldName } from "../../components/uu/config/uuFieldConfig";
import DocumentPreviewDialog from "../../components/uu/uuUi/DocumentPreviewDialog";
import { useAuth } from "@/shared/state/store";
import { UU_SHOW_HIDE_INFO } from "@/shared/uuShowHide";
import { ReferenceHelpConfig, resolveReferenceHelp } from "./referenceHelp";
import { useAlert } from "../../app/providers/AlertProvider";

interface FieldOption {
  value: string | number;
  label: string;
  title?: string;
  description?: string;
}

interface ValidationRule {
  type: string;
  value: string | number;
  message?: string;
  flags?: string;
}

interface ListConfig {
  listFilterQuery?: unknown;
  dynamicSource?: {
    sourceMapping?: unknown[];
  };
}

interface FieldEventActionGroup {
  when?: VisibilityNode;
  actions: unknown[];
}

interface FieldEvents {
  onChange?: FieldEventActionGroup[];
  onMount?: FieldEventActionGroup[];
  onBlur?: FieldEventActionGroup[];
}

interface FieldConfig {
  fieldName: string;
  dataType: string;
  label: string | null;
  // When true on a question-group, auto-inject arrears toggle and arrears dialog
  specialArrearsLogic?: boolean;
  isHidden?: boolean;
  isEditable?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  size?: number;
  maxLength?: number;
  helpText?: string;
  requiredErrorMessage?: string;
  validationRules?: ValidationRule[];
  visibilityCondition?: string;
  visibleWhen?: VisibilityNode;
  requiredWhen?: VisibilityNode;
  disabledWhen?: VisibilityNode;
  valueExpression?: VisibilityNode;
  options?: FieldOption[];
  section?: string;
  list?: ListConfig;
  defaultValue?: unknown;
  order?: number;
  events?: FieldEvents;
  // Custom data types for budget screen
  children?: FieldConfig[];
  tooltipVisible?: boolean;
  tooltipContent?: React.ReactNode;
  showCalculatedIncome?: boolean;
  calculatedIncomeTooltip?: string;
  // List-form specific properties
  showAsCheckBox?: boolean;
  addMore?: boolean;
  formTitle?: string;
  // Field-level help dialog (replaces tooltipContent for detailed help)
  helpDialog?: {
    title?: string;
    description?: string;
    faqs: FAQ[];
  };
  // Help dialog for calculated income section
  calculatedIncomeHelpDialog?: {
    title?: string;
    description?: string;
    faqs: FAQ[];
  };
  // Quick check configuration for conditional validation warnings
  quickCheck?: {
    visibilityCondition: VisibilityNode;
    message: string;
    clickableSegments: Array<{
      text: string;
      action: "openHelpDialog" | "openNotesDialog" | "focusField";
      targetFieldName?: string;
    }>;
  };
  // Additional properties from JSON schema
  addToPayload?: boolean;
  ui?: {
    placeholder?: string;
    helperTop?: string;
    helperAttached?: string;
    helperBottom?: string;
    helperBottomTone?: "default" | "error";
    showErrorBelow?: boolean;
    showErrorOnSection?: boolean;
    searchable?: boolean;
    // Optional label for Select dialog header
    dialogLabel?: string;
    // Select: allow user to type their own value if option not present
    allowCustomEntry?: boolean;
    customEntryLinkText?: string;
    customEntryPlaceholder?: string;
    customEntryConfirmText?: string;
    customEntryCancelText?: string;
    // Separator specific UI knobs (for dataType: 'separator')
    separatorVariant?: "solid" | "dashed";
    separatorThickness?: number;
    separatorColor?: string; // theme color key like 'divider' or 'primary.main'
    separatorSpacingY?: number; // vertical spacing around the line (theme spacing units)
    // TextField: allow user to indicate they don't have a value via checkbox
    allowNoValue?: boolean;
    noValueCheckboxLabel?: string;
    noValueFieldName?: string;
    // SpecialTextField: help dialog for field instructions
    helpLinkText?: string;
    helpDialog?: {
      title: string;
      instructionsTitle?: string;
      steps: Array<{
        stepNumber?: number;
        text: string;
      }>;
      image?: string;
      imageAlt?: string;
      bottomLinkText?: string;
      onBottomLinkClick?: () => void;
    };
    referenceHelp?: ReferenceHelpConfig;
    // InfoBox: display informational message box
    title?: string;
    message?: string;
    messageHtml?: boolean;
    variant?: "info" | "warning";
    // DebtCategory: category type identifier
    categoryType?: string;
    // Counter specific UI knobs (for dataType: 'counter')
    min?: number;
    max?: number;
    limitMessage?: string;
  };
}

export interface FAQ {
  question: string;
  answer: string;
}

interface SectionConfig {
  sectionName: string;
  sectionLabel?: string;
  sectionHelperText?: string;
  sectionHelperTextHtml?: boolean;
  sectionHelperTextWordLimit?: number;
  sectionTooltipContent?: string;
  isArray?: boolean;
  fields: FieldConfig[];
  isTable?: boolean;
  // Optional help topic for this section
  helpTopicId?: string;
  // Optional help dialog with FAQ content
  helpDialog?: {
    title?: string;
    description?: string;
    faqs: FAQ[];
  };
}

// Actions configuration available at the schema level
type SchemaAction =
  | { type: "navigate"; to: string }
  | { type: string; [key: string]: unknown };

interface SchemaButtonsConfig {
  primaryText?: string;
  secondaryText?: string;
  // When true, and the schema has zero fields, show secondary button (e.g. Skip)
  showSecondaryWhenNoFields?: boolean;
  // Currently only supported special action is 'skip' (advance without saving)
  secondaryAction?: "skip";
  // Force show secondary action regardless of field count
  showSecondary?: boolean;
}

interface SchemaActions {
  onSuccess?: SchemaAction[];
  // Optional direct override for the primary button text
  nextButtonText?: string;
  // Optional richer buttons configuration
  buttons?: SchemaButtonsConfig;
}

export interface Schema {
  _id?: { $oid: string };
  formName: string;
  collectionName?: string;
  version: number;
  sections: SectionConfig[];
  isActive?: boolean;
  actions?: SchemaActions;
  // Optional help topic for this form
  helpTopicId?: string;
}

interface ScreenProps {
  schema: Schema;
  onSubmit?: (data: unknown) => void;
  initialData?: Record<string, unknown>;
  hideHeader?: boolean;
  hideActions?: boolean;
  formId?: string;
  // Emits live form values as they change
  onValuesChange?: (data: Record<string, unknown>) => void;
  onWarningAction?: (
    action: OpenActionSpec,
    metadata?: { varId?: string }
  ) => boolean | void;
  // Hide the Paper wrapper around individual fields (useful when rendering inside dialogs)
  hideFieldCards?: boolean;
  /** When true, transforms "council tax" to "council rates" in Quick check warnings */
  isNorthernIreland?: boolean;
}

interface FormData {
  [key: string]: unknown;
}

/**
 * Standalone controlled text input for nested fields inside question-groups.
 * Uses its own `useState` so typing always works regardless of RHF reactivity
 * timing. Syncs to the parent form via the provided callbacks.
 */
interface NestedTextFieldProps {
  fieldName: string;
  initialValue: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  [key: string]: unknown;
}

function NestedTextField({ fieldName, initialValue, onChange: onChangeProp, onBlur: onBlurProp, ...rest }: NestedTextFieldProps) {
  const [localValue, setLocalValue] = useState(initialValue);
  const { showAlert } = useAlert();
  // Keep local state in sync if parent explicitly resets (e.g. clearIfHidden sets to "")
  const prevInitial = useRef(initialValue);
  if (prevInitial.current !== initialValue) {
    prevInitial.current = initialValue;
    setLocalValue(initialValue);
  }
  return (
    <TextField
      name={fieldName}
      value={localValue}
      onKeyDown={(e) => {
        const maxLength = (rest.inputProps as any)?.maxLength;
        if (
          maxLength &&
          localValue.length >= maxLength &&
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey
        ) {
          showAlert({
            severity: "warning",
            message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
          });        }
      }}
      onPaste={(e: any) => {
        const maxLength = (rest.inputProps as any)?.maxLength;
        if (maxLength) {
          const pastedText = e.clipboardData.getData("text");
          const selectionStart = e.target.selectionStart || 0;
          const selectionEnd = e.target.selectionEnd || 0;
          const currentLength = e.target.value.length;
          if (
            currentLength - (selectionEnd - selectionStart) + pastedText.length >
            maxLength
          ) {
            showAlert({
              severity: "warning",
              message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
            });
          }
        }
      }}
      onChange={(e: any) => {
        const val = e?.target?.value ?? '';
        setLocalValue(val);
        onChangeProp(val);
      }}
      onBlur={() => { if (onBlurProp) onBlurProp(); }}
      {...rest}
    />
  );
}

function SpecialScreen({
  schema,
  onSubmit,
  initialData,
  hideHeader,
  hideActions,
  formId = "onboarding-form",
  onValuesChange,
  onWarningAction,
  hideFieldCards = false,
  isNorthernIreland = false,
}: ScreenProps) {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const privacyContent = useMemo(
    () => infoContentSchema.parse(privacyJson),
    []
  );
  // Track latest user-edited values to bypass any stale watchers at submit time
  const livePatchRef = useRef<Record<string, unknown>>({});

  // Track previous form snapshot to detect destructive hide transitions.
  // This powers the "remove values?" confirmation when a toggle hides fields
  // that currently contain user-entered data.
  //
  // NOTE: this MUST be initialized before the first user edit; otherwise the
  // very first Yes -> No toggle has no "before" snapshot to compare against.
  const previousValuesRef = useRef<Record<string, unknown> | null>(null);
  const confirmHiddenClearInFlightRef = useRef(false);

  // Guard to prevent infinite loops during event processing
  const isProcessingEventsRef = useRef(false);

  // Help dialog state - track which section/field's help is open
  const [helpDialogOpen, setHelpDialogOpen] = useState<{
    type: "section" | "field";
    sectionName: string;
    fieldName?: string;
  } | null>(null);

  // Notes dialog state - track which field's notes dialog is open
  const [notesDialogOpen, setNotesDialogOpen] = useState<{
    fieldName: string;
    fieldLabel: string;
    notesFieldName: string;
  } | null>(null);

  // Ensure the red-warning "highlight" style exists on any route that can render QuickCheck.
  // UpdateBudgetPage injects this already; Budget page edit dialogs render SpecialScreen directly.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const styleId = "iehub-warning-highlight-style";
    if (document.getElementById(styleId)) return;

    const styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = `
      .warning-highlight {
        outline: 3px solid #155F54;
        outline-offset: 2px;
        box-shadow: 0 0 0 3px rgba(21, 95, 84, 0.18);
        transition: box-shadow 0.18s ease-in-out, outline 0.18s ease-in-out;
      }
    `;
    document.head.appendChild(styleEl);
  }, []);

  const { user } = useAuth();
  const { showAlert } = useAlert();
  const hideUu = UU_SHOW_HIDE_INFO.uuShowHideInfo;
  const uuController = useUuFieldController({ userId: user?.userId ?? null });
  const uuAmountPrevByFieldRef = useRef<
    Record<string, { raw: string; num: number }>
  >({});
  const uuAmountConfirmInFlightRef = useRef<Record<string, boolean>>({});

  // Helper function to process dynamic UI properties
  const processDynamicValue = useCallback(
    (value: unknown, formData: Record<string, unknown>) => {
      if (
        typeof value === "string" &&
        value.includes("{") &&
        value.includes("}")
      ) {
        // Simple template replacement for {fieldName} patterns
        return value.replace(/\{(\w+)\}/g, (match, fieldName) => {
          const fieldValue = formData[fieldName];
          return fieldValue ? String(fieldValue) : match;
        });
      }
      return value;
    },
    []
  );

  const parseAmountStringToNumber = useCallback((raw: string): number => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return 0;
    const cleaned = trimmed.replace(/,/g, "").replace(/[^\d.-]/g, "");
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  // Helper function to parse simple visibility conditions
  const parseVisibilityCondition = useCallback(
    (condition: string, formData: Record<string, unknown>) => {
      if (!condition) return true;

      try {
        // Handle simple conditions like "(fieldName) !== ''" or "(fieldName) !== 'value'"
        // This is a simplified parser for basic conditions
        const trimmed = condition.trim();

        // Match patterns like (fieldName) !== '' && (fieldName2) !== true
        const complexAndMatch = trimmed.match(
          /^\((\w+)\)\s*!==\s*'([^']*)'\s*&&\s*\((\w+)\)\s*!==\s*(true|false|'[^']*')$/
        );
        if (complexAndMatch) {
          const [, field1, value1, field2, value2] = complexAndMatch;
          const fieldValue1 = field1 ? formData[field1] : undefined;
          const fieldValue2 = field2 ? formData[field2] : undefined;

          // Handle first condition (usually string comparison)
          let condition1: boolean;
          if (value1 === "") {
            condition1 = Boolean(fieldValue1 && fieldValue1 !== "");
          } else {
            condition1 = fieldValue1 !== value1;
          }

          // Handle second condition (could be boolean or string)
          let condition2: boolean;
          if (value2 === "true") {
            condition2 = fieldValue2 !== true;
          } else if (value2 === "false") {
            condition2 = fieldValue2 !== false;
          } else if (value2 && value2.startsWith("'") && value2.endsWith("'")) {
            // Remove quotes for string comparison
            const stringValue = value2.slice(1, -1);
            condition2 = fieldValue2 !== stringValue;
          } else {
            condition2 = fieldValue2 !== value2;
          }

          return condition1 && condition2;
        }

        // Match simple patterns like (fieldName) !== ''
        const simpleNeqMatch = trimmed.match(/^\((\w+)\)\s*!==\s*'([^']*)'$/);
        if (simpleNeqMatch) {
          const [, fieldName, value] = simpleNeqMatch;
          const fieldValue = fieldName ? formData[fieldName] : undefined;

          if (value === "") {
            return Boolean(fieldValue && fieldValue !== "");
          } else {
            return fieldValue !== value;
          }
        }

        // Match patterns like (fieldName) === 'value'
        const eqMatch = trimmed.match(/^\((\w+)\)\s*===\s*'([^']*)'/);
        if (eqMatch) {
          const [, fieldName, expectedValue] = eqMatch;
          return fieldName ? formData[fieldName] === expectedValue : false;
        }

        // Match patterns like (fieldName) !== true or (fieldName) === true
        const boolMatch = trimmed.match(
          /^\((\w+)\)\s*(===|!==)\s*(true|false)$/
        );
        if (boolMatch) {
          const [, fieldName, operator, boolValue] = boolMatch;
          const fieldValue = fieldName ? formData[fieldName] : undefined;
          const expectedBool = boolValue === "true";

          if (operator === "===") {
            return fieldValue === expectedBool;
          } else {
            return fieldValue !== expectedBool;
          }
        }

        // Default to true for unrecognized patterns
        return true;
      } catch (error) {
        console.warn("Error parsing visibility condition:", condition, error);
        return true;
      }
    },
    []
  );

  // Helper to safely evaluate DSL visibility expressions
  // Enhanced to support scoped fields (e.g., __listform_temp_arrears__.companyName)
  const evaluateRule = useCallback(
    (
      rule: VisibilityNode | undefined,
      formData: Record<string, unknown>,
      extraContext: Record<string, unknown> = {},
      scopedPrefix?: string
    ) => {
      if (!rule) return true;
      try {
        // If we have a scoped prefix and the form data contains scoped fields,
        // create an enhanced context that maps unscoped field names to scoped values
        let enhancedData = { ...formData, ...extraContext };

        if (scopedPrefix) {
          // Find all scoped fields in formData and map them to unscoped names
          // This allows visibility conditions like { "var": "companyName" } to work
          // even when the actual field is "__listform_temp_arrears__.companyName"
          Object.keys(formData).forEach((key) => {
            if (key.startsWith(`${scopedPrefix}.`)) {
              // Extract the unscoped field name (e.g., '__listform_temp_arrears__.companyName' -> 'companyName')
              const unscopedName = key.replace(`${scopedPrefix}.`, "");
              // Map unscoped name to scoped value for visibility evaluation
              // Priority: keep existing unscoped value if present, otherwise use scoped value
              if (
                enhancedData[unscopedName] === undefined ||
                enhancedData[unscopedName] === null ||
                enhancedData[unscopedName] === ""
              ) {
                enhancedData[unscopedName] = formData[key];
              }
            }
          });
        }

        return evaluateVisibilityRule(rule, enhancedData);
      } catch {
        return true;
      }
    },
    []
  );

  const computeIsVisible = useCallback(
    (
      field: FieldConfig,
      currentFormValues: Record<string, unknown>,
      scopedPrefix?: string
    ) => {
      if (field.isHidden) return false;
      if (field.visibleWhen !== undefined) {
        return evaluateRule(
          field.visibleWhen,
          currentFormValues,
          {},
          scopedPrefix
        );
      }
      if (field.visibilityCondition) {
        return parseVisibilityCondition(
          field.visibilityCondition,
          currentFormValues
        );
      }
      return true;
    },
    [evaluateRule, parseVisibilityCondition]
  );

  const computeIsDisabled = useCallback(
    (field: FieldConfig, currentFormValues: Record<string, unknown>) => {
      let disabled = Boolean(field.isReadOnly || !field.isEditable);
      if (field.disabledWhen !== undefined) {
        disabled =
          disabled || evaluateRule(field.disabledWhen, currentFormValues);
      }
      return disabled;
    },
    [evaluateRule]
  );

  const computeShouldBeRequired = useCallback(
    (
      field: FieldConfig,
      _hasDynamicBehavior: boolean,
      currentFormValues: Record<string, unknown>
    ) => {
      // Hidden or disabled fields must never be required
      if (!field.isRequired) return false;
      const visible = computeIsVisible(field, currentFormValues);
      const disabled = computeIsDisabled(field, currentFormValues);
      if (!visible || disabled) return false;

      // When a dynamic condition is provided, enforce it only if the field is visible and enabled
      if (field.requiredWhen !== undefined) {
        return evaluateRule(field.requiredWhen, currentFormValues) === true;
      }

      // Otherwise, required when visible and enabled
      return true;
    },
    [evaluateRule, computeIsDisabled, computeIsVisible]
  );

  // triggerFieldEvents is defined after form initialization

  // Create Zod validation schema from field validations
  const createValidationSchema = useCallback(
    (
      sections: SectionConfig[],
      currentFormValues: Record<string, unknown> = {}
    ) => {
      const schemaObj: Record<string, z.ZodTypeAny> = {};

      // Check if this form has any dynamic behavior (visibility/required/disabled rules or events)
      const hasDynamicBehavior = sections.some((section) =>
        section.fields.some((field) =>
          Boolean(
            field.visibilityCondition ||
              field.visibleWhen !== undefined ||
              field.requiredWhen !== undefined ||
              field.disabledWhen !== undefined ||
              (field.events &&
                ((field.events.onChange && field.events.onChange.length > 0) ||
                  (field.events.onBlur && field.events.onBlur.length > 0) ||
                  (field.events.onMount && field.events.onMount.length > 0)))
          )
        )
      );

      // Generate dynamic fields for validation
      const generateFieldsForValidation = () => {
        const allFields: FieldConfig[] = [];

        sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.dataType === "date-with-clear") {
              // Check if this is a dependent DOB field that should be repeated
              if (field.fieldName === "under16DependentsDOB") {
                const under16Count = Number(
                  currentFormValues.under16Dependents || 0
                );
                for (let i = 1; i <= under16Count; i++) {
                  const dynamicField: FieldConfig = {
                    ...field,
                    fieldName: `under16DependentsDOB_${i}`,
                    label: `Dependent Child ${i} Date Of Birth`,
                  };
                  allFields.push(dynamicField);
                }
              } else if (field.fieldName === "between16to18DependentsDOB") {
                const between16to18Count = Number(
                  currentFormValues.between16to18Dependents || 0
                );
                for (let i = 1; i <= between16to18Count; i++) {
                  const dynamicField: FieldConfig = {
                    ...field,
                    fieldName: `between16to18DependentsDOB_${i}`,
                    label: `Dependent Child ${i} Date Of Birth`,
                  };
                  allFields.push(dynamicField);
                }
              } else {
                // Regular date field, add as is
                allFields.push(field);
              }
            } else {
              // Non-date fields, add as is
              allFields.push(field);
            }
          });
        });

        return allFields;
      };

      const fieldsToValidate = generateFieldsForValidation();

      fieldsToValidate.forEach((field) => {
        const isFieldDisabled = hasDynamicBehavior
          ? computeIsDisabled(field, currentFormValues)
          : field.isReadOnly || !field.isEditable;
        const isFieldVisible = hasDynamicBehavior
          ? computeIsVisible(field, currentFormValues)
          : !field.isHidden;

        let fieldSchema: z.ZodTypeAny;

        const shouldBeRequired = computeShouldBeRequired(
          field,
          hasDynamicBehavior,
          currentFormValues
        );

        switch (field.dataType) {
          case "separator":
          case "info-box": {
            // Separator and info-box are UI-only elements; never required and produce no value
            fieldSchema = z.any().optional();
            break;
          }
          case "special-text":
          case "text":
          case "email":
          case "password":
          case "tel":
          case "textarea": {
            let stringSchema = z.string().trim();
            // Apply required validation - use dynamic logic only if form has dynamic behavior
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              stringSchema = stringSchema.min(1, {
                message:
                  field.requiredErrorMessage || `${field.label} is required`,
              });
            }
            if (field.validationRules) {
              field.validationRules.forEach((rule) => {
                switch (rule.type) {
                  case "minLength":
                    if (typeof rule.value === "number") {
                      stringSchema = stringSchema.min(
                        rule.value,
                        rule.message ||
                          `${field.label} must be at least ${rule.value} characters`
                      );
                    }
                    break;
                  case "maxLength":
                    if (typeof rule.value === "number") {
                      stringSchema = stringSchema.max(
                        rule.value,
                        rule.message ||
                          `${field.label} must be at most ${rule.value} characters`
                      );
                    }
                    break;
                  case "pattern":
                    if (typeof rule.value === "string") {
                      // Make pattern optional: allow empty string OR the provided pattern
                      // Wrap original pattern in a non-capturing optional group anchored to full string
                      const optionalWholeStringPattern = `^(?:${rule.value})?$`;
                      const regex = rule.flags
                        ? new RegExp(optionalWholeStringPattern, rule.flags)
                        : new RegExp(optionalWholeStringPattern);
                      stringSchema = stringSchema.regex(
                        regex,
                        rule.message || "Invalid format"
                      );
                    }
                    break;
                }
              });
            }
            fieldSchema = shouldBeRequired
              ? stringSchema
              : z.union([stringSchema, z.literal("")]).optional();

            // Add checkbox field to schema if allowNoValue is enabled
            if (field.ui?.allowNoValue && field.ui?.noValueFieldName) {
              const checkboxFieldName = field.ui.noValueFieldName as string;
              schemaObj[checkboxFieldName] = z.boolean().optional();
            }
            break;
          }

          case "number": {
            let numberSchema = z.number();
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              numberSchema = numberSchema.min(0, {
                message:
                  field.requiredErrorMessage || `${field.label} is required`,
              });
            }
            if (field.validationRules) {
              field.validationRules.forEach((rule) => {
                switch (rule.type) {
                  case "min":
                    if (typeof rule.value === "number") {
                      numberSchema = numberSchema.min(
                        rule.value,
                        rule.message ||
                          `${field.label} must be at least ${rule.value}`
                      );
                    }
                    break;
                  case "max":
                    if (typeof rule.value === "number") {
                      numberSchema = numberSchema.max(
                        rule.value,
                        rule.message ||
                          `${field.label} must be at most ${rule.value}`
                      );
                    }
                    break;
                }
              });
            }
            fieldSchema = shouldBeRequired
              ? numberSchema
              : numberSchema.optional();
            break;
          }

          case "boolean-check":
            fieldSchema = z.boolean();
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              fieldSchema = fieldSchema.refine((val) => val === true, {
                message:
                  field.requiredErrorMessage || `${field.label} is required`,
              });
            } else {
              fieldSchema = fieldSchema.optional();
            }
            break;

          case "select":
          case "multiselect": {
            const requiredMessage =
              field.requiredErrorMessage || `${field.label} is required`;
            fieldSchema =
              field.dataType === "multiselect"
                ? z.array(z.string(), {
                    required_error: requiredMessage,
                    invalid_type_error: requiredMessage,
                  })
                : z.string({ required_error: requiredMessage });
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              fieldSchema = fieldSchema.refine(
                (val) =>
                  Array.isArray(val) ? val.length > 0 : val && val.length > 0,
                {
                  message: requiredMessage,
                }
              );
            } else {
              fieldSchema = (fieldSchema as z.ZodTypeAny).optional();
            }
            break;
          }

          case "debt-category": {
            const debtEntrySchema = z.object({
              id: z.union([z.number(), z.string()]).optional(),
              tempId: z.string().optional(),
              category: z.string().optional(),
              creditorName: z.string().min(1, "Company name is required"),
              parentCompanyName: z.string().nullable().optional(),
              lenderName: z.string().nullable().optional(),
              accTypeCode: z.string().nullable().optional(),
              accTypeId: z.number().nullable().optional(),
              accountTypeName: z.string().nullable().optional(),
              accountNumber: z.string().nullable().optional(),
              amountOwed: z.number().nullable().optional(),
              repaymentAmount: z.number().nullable().optional(),
              repaymentFrequency: z
                .string()
                .min(1, "Repayment frequency is required"),
              paymentOfferAmount: z.number().nullable().optional(),
              paymentOfferFrequency: z.string().nullable().optional(),
              paymentOfferId: z.number().nullable().optional(),
              paymentOfferMode: z.string().nullable().optional(),
              shareBudget: z.string().nullable().optional(),
              shareNow: z.string().nullable().optional(),
              isDigital: z.boolean().optional(),
              schemaName: z.string().nullable().optional(),
              subDomain: z.string().nullable().optional(),
              isPriority: z.boolean().optional(),
              source: z.string().nullable().optional(),
              debtType: z.string().nullable().optional(),
              notes: z.string().nullable().optional(),
              arrearsCode: z.string().nullable().optional(),
            });
            // Do NOT require account/reference number globally; some companies don't provide one.
            // Validation for reference may be handled by DebtCategory UI using company rules.
            let arraySchema = z.array(debtEntrySchema);
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              arraySchema = arraySchema.min(1, {
                message:
                  field.requiredErrorMessage ||
                  `${field.label} requires at least one entry`,
              });
              fieldSchema = arraySchema;
            } else {
              fieldSchema = arraySchema.optional() as z.ZodTypeAny;
            }
            break;
          }

          case "checkbox": {
            // Checkbox can be a group (with options) or a single boolean toggle
            const hasOptions =
              Array.isArray(field.options) && field.options.length > 0;
            if (hasOptions) {
              // Group: array of strings with optional min/max selections and allowed values guard
              const requiredMessage =
                field.requiredErrorMessage || `${field.label} is required`;
              let arraySchema = z.array(z.string(), {
                required_error: requiredMessage,
                invalid_type_error: requiredMessage,
              });

              // Apply min/max selections from validation rules if provided
              let minSel: number | undefined;
              let minSelMsg: string | undefined;
              let maxSel: number | undefined;
              let maxSelMsg: string | undefined;
              if (field.validationRules) {
                field.validationRules.forEach((rule) => {
                  switch (rule.type) {
                    case "minSelections":
                      if (typeof rule.value === "number") {
                        minSel = rule.value;
                        minSelMsg =
                          rule.message ||
                          `Please select at least ${rule.value} option${rule.value > 1 ? "s" : ""}`;
                      }
                      break;
                    case "maxSelections":
                      if (typeof rule.value === "number") {
                        maxSel = rule.value;
                        maxSelMsg =
                          rule.message ||
                          `Please select at most ${rule.value} option${rule.value > 1 ? "s" : ""}`;
                      }
                      break;
                  }
                });
              }

              // If required, ensure at least 1 (or respect higher min from rules)
              if (
                shouldBeRequired &&
                (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
              ) {
                const effectiveMin = Math.max(1, minSel ?? 0);
                arraySchema = arraySchema.min(effectiveMin, {
                  message: minSel ? (minSelMsg as string) : requiredMessage,
                });
              } else if (minSel !== undefined) {
                arraySchema = arraySchema.min(minSel, {
                  message: minSelMsg as string,
                });
              }
              if (maxSel !== undefined) {
                arraySchema = arraySchema.max(maxSel, {
                  message: maxSelMsg as string,
                });
              }

              // Constrain to allowed option values (after min/max so we don't call min on effects)
              const allowedValues = (field.options || [])
                .map((opt) => String((opt as any).value))
                .filter((v) => Boolean(v));
              const constrained = arraySchema.refine(
                (vals) =>
                  Array.isArray(vals) &&
                  vals.every((v) => allowedValues.includes(String(v))),
                { message: field.requiredErrorMessage || "Invalid selection" }
              );

              fieldSchema =
                shouldBeRequired &&
                (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
                  ? constrained
                  : (constrained.optional() as z.ZodTypeAny);
            } else {
              // Single checkbox: boolean required -> must be true
              let boolSchema: z.ZodTypeAny = z.boolean();
              if (
                shouldBeRequired &&
                (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
              ) {
                boolSchema = boolSchema.refine((val) => val === true, {
                  message:
                    field.requiredErrorMessage || `${field.label} is required`,
                });
              } else {
                boolSchema = boolSchema.optional();
              }
              fieldSchema = boolSchema;
            }
            break;
          }

          case "radiogroup": {
            const requiredMessage =
              field.requiredErrorMessage || `${field.label} is required`;
            // Limit to allowed option values to avoid stale values from previous steps (e.g. 'yes')
            const allowedValues = (field.options || [])
              .map((opt) => String((opt as any).value))
              .filter((v) => Boolean(v));

            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              // Require non-empty and that the value is one of the allowed options
              fieldSchema = z
                .string({ required_error: requiredMessage })
                .min(1, { message: requiredMessage })
                .refine(
                  (val) =>
                    allowedValues.length === 0 ||
                    allowedValues.includes(String(val)),
                  {
                    message: requiredMessage,
                  }
                );
            } else {
              // Not required: allow undefined/empty. If provided, it should match an allowed option
              fieldSchema = z
                .string()
                .optional()
                .refine(
                  (val) =>
                    val === undefined ||
                    val === "" ||
                    allowedValues.length === 0 ||
                    allowedValues.includes(String(val)),
                  { message: field.requiredErrorMessage || "Invalid selection" }
                );
            }
            break;
          }

          case "date":
          case "time":
          case "datetime": {
            // Build a robust date schema that distinguishes between required and invalid values
            const requiredMessage =
              field.requiredErrorMessage || `${field.label} is required`;
            const invalidDateMessage = "Please enter a valid date";

            // For required fields, convert null -> undefined so required_error is shown instead of invalid type
            const buildBaseDateSchema = (isRequired: boolean) => {
              if (isRequired) {
                return z.preprocess(
                  (val) => (val === null ? undefined : val),
                  z.date({
                    required_error: requiredMessage,
                    invalid_type_error: invalidDateMessage,
                  })
                );
              }
              // Optional field: allow undefined; still show invalid message when an invalid date object is present
              return z.preprocess(
                (val) => (val === null ? undefined : val),
                z.date({ invalid_type_error: invalidDateMessage }).optional()
              );
            };

            let dateSchema: z.ZodTypeAny = buildBaseDateSchema(
              shouldBeRequired &&
                (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            );

            // Apply age constraints from validationRules, if provided
            if (
              Array.isArray(field.validationRules) &&
              field.validationRules.length > 0
            ) {
              let minAge: number | undefined;
              let minAgeMsg: string | undefined;
              let maxAge: number | undefined;
              let maxAgeMsg: string | undefined;

              field.validationRules.forEach((rule) => {
                switch (rule.type) {
                  case "minAge":
                    if (typeof rule.value === "number") {
                      minAge = rule.value;
                      minAgeMsg =
                        rule.message ||
                        `Must be at least ${rule.value} years old`;
                    }
                    break;
                  case "maxAge":
                    if (typeof rule.value === "number") {
                      maxAge = rule.value;
                      maxAgeMsg =
                        rule.message ||
                        `Must be no more than ${rule.value} years old`;
                    }
                    break;
                }
              });

              if (typeof minAge === "number" && Number.isFinite(minAge)) {
                dateSchema = dateSchema.refine(
                  (d: unknown) => {
                    if (!(d instanceof Date)) return true; // Type error handled earlier
                    const today = new Date();
                    const ageYears = calculateAge(d, today);
                    return (
                      typeof ageYears === "number" &&
                      ageYears >= (minAge as number)
                    );
                  },
                  { message: minAgeMsg as string }
                );
              }

              if (typeof maxAge === "number" && Number.isFinite(maxAge)) {
                dateSchema = dateSchema.refine(
                  (d: unknown) => {
                    if (!(d instanceof Date)) return true; // Type error handled earlier
                    const today = new Date();
                    const ageYears = calculateAge(d, today);
                    return (
                      typeof ageYears === "number" &&
                      ageYears <= (maxAge as number)
                    );
                  },
                  { message: maxAgeMsg as string }
                );
              }
            }

            fieldSchema = dateSchema as z.ZodTypeAny;
            break;
          }

          case "radio": {
            let stringSchema = z.string();
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              stringSchema = stringSchema.min(1, {
                message:
                  field.requiredErrorMessage || `${field.label} is required`,
              });
            }
            fieldSchema = shouldBeRequired
              ? stringSchema
              : stringSchema.optional();
            break;
          }

          case "toggle-button": {
            let booleanSchema = z.boolean();
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              // For toggle-button, we require a boolean value (true or false)
              fieldSchema = booleanSchema;
            } else {
              fieldSchema = booleanSchema.optional();
            }
            break;
          }

          case "only-label": {
            // Only-label fields don't have form values, so we skip validation
            fieldSchema = z.any().optional();
            break;
          }

          case "counter": {
            let numberSchema = z.number().int().min(0);
            if (
              shouldBeRequired &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              numberSchema = numberSchema.min(1, {
                message:
                  field.requiredErrorMessage || `${field.label} is required`,
              });
            }
            fieldSchema = shouldBeRequired
              ? numberSchema
              : numberSchema.optional();
            break;
          }

          case "date-with-clear": {
            // Build a robust date schema that distinguishes between required and invalid values
            const requiredMessage =
              field.requiredErrorMessage || `${field.label} is required`;
            const invalidDateMessage = "Please enter a valid date";

            // For required fields, convert null -> undefined so required_error is shown instead of invalid type
            const buildBaseDateSchema = (isRequired: boolean) => {
              if (isRequired) {
                return z.preprocess(
                  (val) => (val === null ? undefined : val),
                  z.date({
                    required_error: requiredMessage,
                    invalid_type_error: invalidDateMessage,
                  })
                );
              }
              // Optional field: allow undefined; still show invalid message when an invalid date object is present
              return z.preprocess(
                (val) => (val === null ? undefined : val),
                z.date({ invalid_type_error: invalidDateMessage }).optional()
              );
            };

            let dateSchema: z.ZodTypeAny = buildBaseDateSchema(
              shouldBeRequired &&
                (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            );

            // Apply age constraints from validationRules, if provided
            if (
              Array.isArray(field.validationRules) &&
              field.validationRules.length > 0
            ) {
              let minAge: number | undefined;
              let minAgeMsg: string | undefined;
              let maxAge: number | undefined;
              let maxAgeMsg: string | undefined;

              field.validationRules.forEach((rule) => {
                switch (rule.type) {
                  case "minAge":
                    if (typeof rule.value === "number") {
                      minAge = rule.value;
                      minAgeMsg =
                        rule.message ||
                        `Must be at least ${rule.value} years old`;
                    }
                    break;
                  case "maxAge":
                    if (typeof rule.value === "number") {
                      maxAge = rule.value;
                      maxAgeMsg =
                        rule.message ||
                        `Must be no more than ${rule.value} years old`;
                    }
                    break;
                }
              });

              if (typeof minAge === "number" && Number.isFinite(minAge)) {
                dateSchema = dateSchema.refine(
                  (d: unknown) => {
                    if (!(d instanceof Date)) return true; // Type error handled earlier
                    const today = new Date();
                    const ageYears = calculateAge(d, today);
                    return (
                      typeof ageYears === "number" &&
                      ageYears >= (minAge as number)
                    );
                  },
                  { message: minAgeMsg as string }
                );
              }

              if (typeof maxAge === "number" && Number.isFinite(maxAge)) {
                dateSchema = dateSchema.refine(
                  (d: unknown) => {
                    if (!(d instanceof Date)) return true; // Type error handled earlier
                    const today = new Date();
                    const ageYears = calculateAge(d, today);
                    return (
                      typeof ageYears === "number" &&
                      ageYears <= (maxAge as number)
                    );
                  },
                  { message: maxAgeMsg as string }
                );
              }
            }

            fieldSchema = dateSchema as z.ZodTypeAny;
            break;
          }

          case "count-label": {
            // Count-label fields are display-only, so we skip validation
            fieldSchema = z.any().optional();
            break;
          }

          case "list-form": {
            // List-form fields store an array of form items
            // The validation is flexible as the form structure is dynamic
            let arraySchema = z.array(z.any());

            // If showAsCheckBox is true and field is required, at least one item must be added when checked
            if (
              shouldBeRequired &&
              field.showAsCheckBox &&
              (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
            ) {
              arraySchema = arraySchema.min(1, {
                message:
                  field.requiredErrorMessage || "Please add at least one item",
              });
            }

            fieldSchema = shouldBeRequired
              ? arraySchema
              : arraySchema.optional();
            break;
          }

          default:
            fieldSchema = z.any();
        }

        schemaObj[field.fieldName] = fieldSchema;
      });

      return z.object(schemaObj);
    },
    [parseVisibilityCondition]
  );

  // Check if this form needs dynamic validation
  const needsDynamicValidation = useMemo(
    () =>
      schema.sections.some((section) =>
        section.fields.some((field) =>
          Boolean(
            field.visibilityCondition ||
              field.visibleWhen !== undefined ||
              field.requiredWhen !== undefined ||
              field.disabledWhen !== undefined ||
              (field.events &&
                ((field.events.onChange && field.events.onChange.length > 0) ||
                  (field.events.onBlur && field.events.onBlur.length > 0) ||
                  (field.events.onMount && field.events.onMount.length > 0)))
          )
        )
      ),
    [schema.sections]
  );

  // Create initial validation schema
  const initialValidationSchema = useMemo(
    () => createValidationSchema(schema.sections),
    [schema.sections, createValidationSchema]
  );

  const resolverRef = useRef<Resolver<FormData>>(
    zodResolver(initialValidationSchema)
  );
  const resolver = useCallback<Resolver<FormData>>(
    (values, context, options) => {
      return resolverRef.current(values, context, options);
    },
    []
  );

  // Create default values from schema and merge with initial data
  const createDefaultValues = useCallback((sections: SectionConfig[]) => {
    const defaults: Record<string, unknown> = {};

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          defaults[field.fieldName] = field.defaultValue;
        }
        // Handle children fields with dot notation
        if (field.children && Array.isArray(field.children)) {
          field.children.forEach((childField) => {
            const nestedFieldName = `${field.fieldName}.${childField.fieldName}`;
            if (childField.defaultValue !== undefined) {
              defaults[nestedFieldName] = childField.defaultValue;
            }
          });
        }
      });
    });

    return defaults;
  }, []);

  // Flatten nested objects in initialData to match dot notation field names
  const flattenInitialData = useCallback(
    (data: Record<string, unknown> | undefined): Record<string, unknown> => {
      if (!data) return {};

      const result: Record<string, unknown> = {};

      const recurse = (
        obj: Record<string, unknown>,
        parentPath: string | null
      ) => {
        Object.entries(obj).forEach(([k, v]) => {
          const path = parentPath ? `${parentPath}.${k}` : k;
          // Only recurse into plain objects (exclude Date, Array, null)
          if (
            v &&
            typeof v === "object" &&
            !Array.isArray(v) &&
            !(v instanceof Date)
          ) {
            recurse(v as Record<string, unknown>, path);
          } else {
            result[path] = v as unknown;
          }
        });
      };

      recurse(data, null);
      return result;
    },
    []
  );

  const schemaDefaults = useMemo(
    () => createDefaultValues(schema.sections),
    [schema.sections, createDefaultValues]
  );

  // Collect the fully qualified field paths that belong to the current schema
  // (includes children as dot-paths) so we can tell when real form data, not
  // just metadata, has arrived.
  const schemaFieldPaths = useMemo(() => {
    const names = new Set<string>();
    schema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        names.add(field.fieldName);
        if (Array.isArray(field.children)) {
          field.children.forEach((child) => {
            names.add(`${field.fieldName}.${child.fieldName}`);
          });
        }
      });
    });
    return names;
  }, [schema.sections]);

  const flattenedInitialData = useMemo(
    () => flattenInitialData(initialData),
    [initialData, flattenInitialData]
  );
  const formDefaults = useMemo(() => {
    const merged = { ...schemaDefaults, ...flattenedInitialData };
    return merged;
  }, [schemaDefaults, flattenedInitialData]);

  const form = useForm<FormData>({
    resolver,
    defaultValues: formDefaults,
  });

  const mergeWithLivePatch = useCallback(
    (values: Record<string, unknown>): Record<string, unknown> => {
      const patches = livePatchRef.current;
      if (!patches || Object.keys(patches).length === 0) {
        return values;
      }

      const merged: Record<string, unknown> = { ...values };

      Object.entries(patches).forEach(([key, patchValue]) => {
        if (key.includes(".")) {
          merged[key] = patchValue;
          return;
        }

        const current = merged[key];
        const isPatchObject =
          patchValue !== null &&
          typeof patchValue === "object" &&
          !Array.isArray(patchValue);
        const isCurrentObject =
          current !== null &&
          typeof current === "object" &&
          !Array.isArray(current);

        if (isPatchObject) {
          merged[key] = {
            ...(isCurrentObject ? (current as Record<string, unknown>) : {}),
            ...(patchValue as Record<string, unknown>),
          };
        } else {
          merged[key] = patchValue;
        }
      });

      return merged;
    },
    []
  );

  // Ensure we have an initial "previous snapshot" before the user makes their first change.
  // Without this, the first destructive Yes -> No toggle won't prompt (no baseline to diff).
  useEffect(() => {
    if (previousValuesRef.current) return;
    try {
      const raw = (form.getValues ? form.getValues() : {}) as Record<string, unknown>;
      previousValuesRef.current = mergeWithLivePatch(raw);
    } catch {
      // ignore
    }
  }, [form, mergeWithLivePatch]);

  const isObjectRecord = useCallback(
    (value: unknown): value is Record<string, unknown> =>
      typeof value === "object" && value !== null && !Array.isArray(value),
    []
  );

  /**
   * UU document prompts should only appear when the UU UI/context is actually active.
   * This prevents "delete uploaded document" popups after a user removes United Utilities
   * as their referred company (UU components become hidden, but documents may still exist).
   */
  const isUuReferralActive = useCallback((): boolean => {
    if (hideUu) return false;

    // Prefer live form values when available
    try {
      const rawValues: FormData = form.getValues ? form.getValues() : {};
      const latestValues = mergeWithLivePatch(rawValues);

      const notReferred = latestValues.notReferredByCompany === true;
      if (notReferred) return false;

      const referredCompany = latestValues.referedFromCompany;
      if (typeof referredCompany === "string") {
        const lower = referredCompany.trim().toLowerCase();
        if (lower.length > 0) {
          return lower.includes("united utilit");
        }
      }
    } catch {
      // ignore and fall back to cached customer data
    }

    // Fallback: use cached customerData (may be missing on some screens)
    try {
      const storedCustomer = localStorage.getItem("customerData");
      if (!storedCustomer) return false;
      const parsed: unknown = JSON.parse(storedCustomer);
      if (!isObjectRecord(parsed)) return false;
      const field5TxtFld =
        typeof parsed.field5TxtFld === "string"
          ? parsed.field5TxtFld.trim().toLowerCase()
          : "";
      return field5TxtFld.includes("united utilit");
    } catch {
      return false;
    }
  }, [form, hideUu, isObjectRecord, mergeWithLivePatch]);

  /**
   * Pre-submit guard: prevent submitting UU amounts as 0/blank when a UU document exists,
   * unless user confirms deletion. This closes the "click submit while still focused" gap.
   *
   * Returns true to continue submission; false to abort submission.
   */
  // --- Inline UU doc guard DISABLED ---
  // Evidence + document cleanup is now handled at save-time in
  // updateBudgetPage.tsx / EditFieldDialog.tsx via uuEvidenceCleanup.ts.
  // This avoids double-prompting the user.
  const ensureUuDocGuardBeforeSubmit = useCallback(async (): Promise<boolean> => {
    return true;
  }, [
    uuController,
  ]);

  const syncNestedFieldState = useCallback(
    (fieldPath: string, childValue: unknown) => {
      if (!fieldPath) {
        return;
      }

      livePatchRef.current[fieldPath] = childValue;

      if (!fieldPath.includes(".")) {
        return;
      }

      const parts = fieldPath.split(".");
      const childKey = parts.pop();
      const parentPath = parts.join(".");

      if (!childKey || !parentPath) {
        return;
      }

      try {
        const currentGroup = form.getValues
          ? ((form.getValues(parentPath) as
              | Record<string, unknown>
              | undefined) ?? {})
          : {};
        const nextGroup = {
          ...currentGroup,
          [childKey]: childValue,
        } as Record<string, unknown>;

        if (form.setValue) {
          (form.setValue as any)(parentPath, nextGroup, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });
        }

        const previousParentPatch =
          (livePatchRef.current[parentPath] as
            | Record<string, unknown>
            | undefined) ?? {};
        livePatchRef.current[parentPath] = {
          ...previousParentPatch,
          [childKey]: childValue,
        };
      } catch (error) {
        console.warn("Failed to sync nested field state", { fieldPath, error });
      }
    },
    [form]
  );

  // Normalize supplier values coming from API to ensure "Other" maps correctly
  // This guarantees dependent "*SupplierOther" fields become visible after reload.
  useEffect(() => {
    try {
      const normalizeValue = (val: unknown): string | unknown => {
        if (typeof val !== "string") return val;
        const lower = val.trim().toLowerCase();
        const cleaned = lower.replace(/\s+/g, " ");
        if (
          cleaned === "other" ||
          cleaned.includes("cannot find") ||
          cleaned.includes("can't find") ||
          cleaned.includes("cannot-find") ||
          cleaned.includes("cant find") ||
          cleaned.includes("referred company")
        ) {
          return "Other";
        }
        return val;
      };
      const deepClone = (obj: any): any => {
        if (obj === null || typeof obj !== "object") return obj;
        if (Array.isArray(obj)) return obj.map(deepClone);
        const out: any = {};
        Object.keys(obj).forEach((k) => {
          out[k] = deepClone(obj[k]);
        });
        return out;
      };
      const normalizeObject = (obj: any): any => {
        if (obj && typeof obj === "object") {
          const result: any = Array.isArray(obj) ? [] : {};
          Object.keys(obj).forEach((k) => {
            const v = (obj as any)[k];
            if (v && typeof v === "object") {
              result[k] = normalizeObject(v);
            } else {
              result[k] = k.endsWith("Supplier") ? normalizeValue(v) : v;
            }
          });
          return result;
        }
        return obj;
      };
      const current = form.getValues
        ? (form.getValues() as Record<string, unknown>)
        : {};
      const next = normalizeObject(deepClone(current));
      const changed = JSON.stringify(next) !== JSON.stringify(current);
      if (changed) {
        form.reset(next as any, {
          keepDirty: true,
          keepTouched: true,
          keepSubmitCount: true,
        });
        setTimeout(() => {
          try {
            form.trigger();
          } catch {
            /* ignore */
          }
        }, 0);
      }
    } catch {
      // ignore
    }
    // run once after init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Track initialized checkbox fields for special-text allowNoValue feature
  const initializedCheckboxFields = useRef<Set<string>>(new Set());
  // Throttle blocked-input alert for referenceId/referenceNumber/accountNumber fields
  const lastApostropheAlertAtRef = useRef<number>(0);
  const isAccountField = useCallback((name: string): boolean => {
    if (!name) return false;
    const isBase =
      name === "referenceId" ||
      name === "referenceNumber" ||
      name === "accountNumber";
    if (isBase) return true;
    return (
      name.endsWith(".referenceId") ||
      name.endsWith(".referenceNumber") ||
      name.endsWith(".accountNumber")
    );
  }, []);

  const triggerBlockedInputAlert = useCallback((isOverLimit?: boolean) => {
    const now = Date.now();
    if (now - lastApostropheAlertAtRef.current < 1500) {
      return;
    }
    lastApostropheAlertAtRef.current = now;
    const message = isOverLimit
      ? UI_MESSAGES.CHARACTER_LIMIT_REACHED
      : UI_MESSAGES.APOSTROPHE_NOT_ALLOWED;

    try {
      window.dispatchEvent(
        new CustomEvent("iehub-alert", {
          detail: {
            severity: "warning",
            message: message,
            variant: "filled",
            duration: 3000,
          },
        })
      );
    } catch {
      // no-op if dispatch fails
    }
  }, []);

  // Local tick to force re-render when form values change (ensures visibleWhen/disabledWhen re-evaluation)
  // Local state tick to force re-render when form values change (ensures visibleWhen/requiredWhen updates)
  const [, forceRender] = useState(0);

  // Guard to prevent infinite recursive loop in clearIfHidden:
  // clearIfHidden → form.setValue → watch fires → clearIfHidden → form.setValue → ...
  const isClearingHiddenFieldsRef = useRef(false);

  // Track if initial data has been set
  const hasInitialDataRef = useRef(false);

  // Reset form when initialData changes from empty to populated (initial load only)
  useEffect(() => {
    // Only treat initial data as "arrived" when at least one schema field (not
    // just metadata injected for visibility) has a value.
    const hasSchemaData = Object.entries(flattenedInitialData).some(
      ([key, value]) => {
        if (!schemaFieldPaths.has(key)) return false;
        if (value === undefined || value === null) return false;
        if (typeof value === "string") {
          // Accept "0" and non-empty strings as data
          return value.trim().length > 0 || value === "0";
        }
        return true;
      }
    );

    if (hasSchemaData && !hasInitialDataRef.current) {
      hasInitialDataRef.current = true;
      form.reset(formDefaults);
      // IMPORTANT:
      // On first hydrate (API → initialData), React Hook Form may not trigger the
      // `watch` subscription until the user edits something.
      //
      // UpdateBudgetPage relies on `onValuesChange` to persist a snapshot into Zustand
      // and to populate `liveBudgetFormValuesRef` so auto-save-on-navigation does not
      // accidentally submit `{}` and wipe dependent answers (e.g. leisure gambling).
      if (typeof onValuesChange === "function") {
        try {
          const rawValues = (form.getValues
            ? form.getValues()
            : {}) as Record<string, unknown>;
          const latestValues = mergeWithLivePatch(rawValues);
          onValuesChange(latestValues);
        } catch {
          // no-op
        }
      }

      // Seed the previous snapshot from hydrated defaults so the first user toggle
      // can correctly compare "before" vs "after" and show the confirm immediately.
      try {
        previousValuesRef.current = mergeWithLivePatch(formDefaults as unknown as Record<string, unknown>);
      } catch {
        // ignore
      }
    }
  }, [
    flattenedInitialData,
    formDefaults,
    form,
    schemaFieldPaths,
    onValuesChange,
    mergeWithLivePatch,
  ]);

  /**
   * Sync "visibility metadata" fields into the live form state whenever they change.
   *
   * Why:
   * - UpdateBudgetPage merges `customerMetadata` into `initialData` so IE visibility rules can
   *   reference fields like `accommodationType`, `jointAccount`, etc.
   * - This renderer intentionally avoids a full `form.reset()` after initial hydrate to prevent
   *   wiping user-entered budget values.
   * - Result: metadata-driven visibility can remain stale until a full page refresh.
   *
   * Fix:
   * - For a small set of known metadata keys (not part of the schema), push updated values into
   *   RHF via `setValue` without marking the form dirty.
   */
  const lastSyncedVisibilityMetaRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const META_KEYS: string[] = [
      "jointAccount",
      "customerEmploymentStatus",
      "partnerEmploymentStatus",
      "haveChildrenUnder18",
      "accommodationType",
      "customerAge",
      "partnerAge",
      "dependentChildrenUnder16",
      "dependentChildrenAged16Plus",
    ];

    let didUpdate = false;

    META_KEYS.forEach((key) => {
      const keyStr = key;

      // If a schema ever defines these names, do NOT overwrite user input.
      if (schemaFieldPaths.has(keyStr)) return;

      const nextVal = (flattenedInitialData as Record<string, unknown>)[keyStr];
      const nextSig = (() => {
        try {
          return JSON.stringify(nextVal ?? null);
        } catch {
          return String(nextVal ?? "");
        }
      })();

      if (lastSyncedVisibilityMetaRef.current[keyStr] === nextSig) return;
      lastSyncedVisibilityMetaRef.current[keyStr] = nextSig;

      try {
        form.setValue(keyStr as any, nextVal as any, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        didUpdate = true;
      } catch {
        // ignore if setValue fails for any reason
      }
    });

    // Ensure visibility/required/disabled rules re-evaluate even if RHF doesn't
    // notify watchers for unregistered metadata fields.
    if (didUpdate) {
      forceRender((prev) => prev + 1);
    }
  }, [flattenedInitialData, form, schemaFieldPaths]);

  const toggleFieldNames = useMemo(() => {
    const names: string[] = [];
    schema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.dataType === "toggle-button" && typeof field.fieldName === "string") {
          names.push(field.fieldName);
        }
      });
    });
    return names;
  }, [schema.sections]);

  // Subscribe to form value changes: trigger re-render and bubble up values when provided
  useEffect(() => {
    const subscription = (form as any).watch(
      (_value: Record<string, unknown>) => {
        const latestValues = (
          form.getValues ? form.getValues() : _value
        ) as Record<string, unknown>;
        // Always force re-render to update UI (this is safe and necessary)
        forceRender((prev) => prev + 1);

        // Skip clearing hidden fields if we're currently processing events, confirming,
        // or already inside a clearIfHidden pass (prevents infinite recursive loop:
        // clearIfHidden → setValue → watch fires → clearIfHidden → setValue → ...)
        if (!isProcessingEventsRef.current && !confirmHiddenClearInFlightRef.current && !isClearingHiddenFieldsRef.current) {
          const getAtPath = (obj: Record<string, unknown>, path: string): unknown => {
            if (!path) return obj;
            // Prefer exact match when livePatchRef stores dot-keys.
            if (Object.prototype.hasOwnProperty.call(obj, path)) return (obj as any)[path];
            return path.split(".").reduce<unknown>((acc, key) => {
              if (acc && typeof acc === "object" && !Array.isArray(acc)) {
                return (acc as any)[key];
              }
              return undefined;
            }, obj);
          };

          const coerceBool = (v: unknown): boolean => {
            if (v === true) return true;
            if (v === false) return false;
            if (typeof v === "string") {
              const t = v.trim().toLowerCase();
              if (t === "true" || t === "y" || t === "yes") return true;
              if (t === "false" || t === "n" || t === "no") return false;
            }
            return Boolean(v);
          };

          const hasMeaningfulValue = (v: unknown, fieldPath: string): boolean => {
            // For amount-like fields, treat any non-blank numeric amount (including 0) as meaningful.
            //
            // Rationale: across IEHub budget screens we explicitly ask users to enter 0 (not blank)
            // when something doesn't apply. A stored amount of 0 is therefore still user-entered
            // data, and turning off a section toggle should warn before clearing it.
            if (fieldPath.endsWith(".amount")) {
              if (typeof v === "number") return Number.isFinite(v);
              if (typeof v === "string") {
                const cleaned = v.replace(/,/g, "").replace(/£/g, "").trim();
                if (!cleaned) return false;
                const n = Number(cleaned);
                return Number.isFinite(n);
              }
              return false;
            }

            if (typeof v === "string") return v.trim().length > 0;
            if (typeof v === "number") return Number.isFinite(v) && v !== 0;
            if (typeof v === "boolean") return true;
            return v !== undefined && v !== null;
          };

          const previous = previousValuesRef.current;
          if (previous) {
            // Only prompt on destructive transitions initiated via section toggles (Yes -> No).
            const togglesTurnedOff = toggleFieldNames.filter((name) => {
              const before = coerceBool(getAtPath(previous, name));
              const after = coerceBool(getAtPath(latestValues, name));
              return before === true && after === false;
            });

            const newlyHiddenFields: FieldConfig[] = [];
            const newlyHiddenWithData: FieldConfig[] = [];

            if (togglesTurnedOff.length > 0) {
              schema.sections.forEach((section) => {
                section.fields.forEach((field) => {
                  const f = field as FieldConfig;
                  // Skip the toggles themselves; we care about the dependent fields being hidden.
                  if (f.dataType === "toggle-button") return;

                  const beforeVisible = computeIsVisible(f, previous);
                  const afterVisible = computeIsVisible(f, latestValues);
                  if (!(beforeVisible && !afterVisible)) return;

                  newlyHiddenFields.push(f);

                  // Determine if the field contains user-entered data (amount>0 or non-empty notes, etc.)
                  if (f.dataType === "question-group" && Array.isArray(f.children)) {
                    const groupPath = f.fieldName;
                    const hasData = f.children.some((child) => {
                      const childPath = `${groupPath}.${child.fieldName}`;
                      const val = getAtPath(previous, childPath as string);
                      return hasMeaningfulValue(val, childPath);
                    });
                    if (hasData) newlyHiddenWithData.push(f);
                    return;
                  }

                  const currentVal = getAtPath(previous, f.fieldName);
                  if (hasMeaningfulValue(currentVal, f.fieldName)) {
                    newlyHiddenWithData.push(f);
                  }
                });
              });
            }

            if (newlyHiddenWithData.length > 0) {
              confirmHiddenClearInFlightRef.current = true;

              void (async () => {
                try {
                  const confirmed = await openConfirmDialog({
                    title: "Remove saved information?",
                    message:
                      "Changing this will clear any information entered in this section. Do you want to continue?",
                    confirmText: "Remove",
                    cancelText: "Keep",
                  });

                  // Clear any live patches so they don't re-introduce old values after setValue/reset
                  livePatchRef.current = {};

                  if (!confirmed) {
                    // Revert the toggle change (and any dependent changes) back to the previous snapshot.
                    form.reset(previous as any, { keepDirty: false, keepTouched: false });
                    return;
                  }

                  // Confirmed: clear all newly hidden fields (including those without data).
                  isProcessingEventsRef.current = true;
                  for (const f of newlyHiddenFields) {
                    if (f.dataType === "question-group" && Array.isArray(f.children)) {
                      const groupPath = f.fieldName;
                      for (const child of f.children) {
                        const childPath = `${groupPath}.${child.fieldName}`;
                        try {
                          form.setValue(childPath, "", { shouldValidate: false });
                        } catch {
                          // ignore
                        }
                      }
                      continue;
                    }

                    // For non-group fields, clear the field itself.
                    if (f.fieldName) {
                      try {
                        form.setValue(f.fieldName, "", { shouldValidate: false });
                      } catch {
                        // ignore
                      }
                    }
                  }
                } finally {
                  // Allow watch cycle to proceed after form state settles.
                  setTimeout(() => {
                    isProcessingEventsRef.current = false;
                    confirmHiddenClearInFlightRef.current = false;
                    try {
                      const raw = (form.getValues ? form.getValues() : {}) as Record<string, unknown>;
                      previousValuesRef.current = mergeWithLivePatch(raw);
                    } catch {
                      // ignore
                    }
                  }, 0);
                }
              })();

              // Abort the default hidden-field clearing for this tick; the async confirm flow will handle it.
              return;
            }
          }

          // Default behavior: clear values for fields that are hidden by visibility conditions.
          // Enhanced: traverse into question-group children and clear hidden child fields as well.
          const clearIfHidden = (
            fieldToCheck: FieldConfig,
            currentValues: Record<string, unknown>,
            parentPath?: string
          ) => {
            if (
              fieldToCheck.dataType === "question-group" &&
              Array.isArray(fieldToCheck.children)
            ) {
              const groupPath = parentPath
                ? `${parentPath}.${fieldToCheck.fieldName}`
                : fieldToCheck.fieldName;
              fieldToCheck.children.forEach((child) => {
                const effectiveChild: FieldConfig = {
                  ...child,
                  fieldName: `${groupPath}.${child.fieldName}`,
                } as FieldConfig;
                clearIfHidden(effectiveChild, currentValues);
              });
              return;
            }

            const isVisible = computeIsVisible(fieldToCheck, currentValues);
            if (!isVisible) {
              const currentVal = getAtPath(currentValues, fieldToCheck.fieldName);
              if (currentVal !== undefined && currentVal !== null && currentVal !== "") {
                try {
                  form.setValue(fieldToCheck.fieldName, "", { shouldValidate: false });
                  // Ensure the live patch does not resurrect the cleared value.
                  livePatchRef.current[fieldToCheck.fieldName] = "";
                } catch {
                  // ignore
                }
              }
            }
          };

          isClearingHiddenFieldsRef.current = true;
          try {
            schema.sections.forEach((section) => {
              section.fields.forEach((field) =>
                clearIfHidden(field as FieldConfig, latestValues)
              );
            });
          } finally {
            isClearingHiddenFieldsRef.current = false;
          }
        }

        if (onValuesChange) {
          try {
            const augmentedValues = mergeWithLivePatch(latestValues);
            onValuesChange(augmentedValues);
          } catch {
            // no-op
          }
        }

        // Debug snapshot for salary amount/frequency
        try {
          /* swallow debug */
        } catch {}

        // Update previous snapshot after all side-effects (unless a confirm is in-flight).
        if (!confirmHiddenClearInFlightRef.current) {
          previousValuesRef.current = mergeWithLivePatch(latestValues);
        }
      }
    );
    return () => {
      if (
        subscription &&
        typeof (subscription as any).unsubscribe === "function"
      ) {
        (subscription as any).unsubscribe();
      }
    };
  }, [
    form,
    onValuesChange,
    schema.sections,
    computeIsVisible,
    mergeWithLivePatch,
  ]);

  const triggerFieldEvents = useCallback(
    async (
      eventName: keyof FieldEvents,
      field: FieldConfig,
      newValue: unknown
    ) => {
      const groups = field.events?.[eventName];
      if (!groups || groups.length === 0) return;

      // Prevent recursive event triggering
      if (isProcessingEventsRef.current) {
        return;
      }

      try {
        isProcessingEventsRef.current = true;
        const currentValues = form.getValues
          ? form.getValues()
          : ({} as Record<string, unknown>);
        const context = {
          ...currentValues,
          $value: newValue,
          $field: field.fieldName,
        } as Record<string, unknown>;

        for (const group of groups) {
          const shouldRun = group.when
            ? evaluateRule(group.when, context)
            : true;
          if (!shouldRun) continue;
          await runEffects(group.actions, {
            navigate,
            formApi: {
              setValue: (name, value, options) =>
                form.setValue(name as string, value as unknown, options as any),
              reset: (values, options) =>
                form.reset(values as any, options as any),
              trigger: (name) =>
                form.trigger(name as string | string[] | undefined),
            },
          });
        }
      } finally {
        // Use setTimeout to ensure the flag is cleared after all setValue calls complete
        setTimeout(() => {
          isProcessingEventsRef.current = false;
        }, 0);
      }
    },
    [evaluateRule, form, navigate]
  );

  // Watch form values only if dynamic validation is needed
  const formValues = needsDynamicValidation ? form.watch() : {};
  const validationSchema = useMemo(() => {
    if (needsDynamicValidation) {
      return createValidationSchema(schema.sections, formValues);
    }
    return initialValidationSchema;
  }, [
    schema.sections,
    createValidationSchema,
    formValues,
    needsDynamicValidation,
    initialValidationSchema,
  ]);

  useEffect(() => {
    resolverRef.current = zodResolver(validationSchema) as Resolver<FormData>;
  }, [validationSchema]);

  const handleSubmit = useCallback(
    async (data: FormData) => {
      console.log("🔥 SpecialScreen handleSubmit called with data:", data);
      setLoading(true);
      try {
        // Always validate against the current schema to ensure correctness across step changes
        console.log("🔥 Running validation against schema...");
        const result = validationSchema.safeParse(data);
        console.log("🔥 Validation result:", result.success);

        if (!result.success) {
          console.log("🔥 Validation failed with errors:", result.error.errors);
          // Set validation errors
          const errors: Record<string, { message: string }> = {};
          result.error.errors.forEach((error) => {
            if (error.path.length > 0) {
              errors[error.path[0] as string] = { message: error.message };
            }
          });
          console.log("🔥 Setting form errors:", errors);
          // Apply errors to form controls
          Object.entries(errors).forEach(([field, error]) => {
            form.setError(field, error);
          });
          setLoading(false);
          console.log("🔥 Validation failed - returning early");
          return;
        }

        console.log("🔥 Validation passed - proceeding with submission");

        // Clean up the data before submission
        const cleanedData = { ...data };

        // If "Not Referred By a Company" is checked, ensure company field is cleared
        if (
          cleanedData.notReferredByCompany === true &&
          typeof cleanedData.referedFromCompany === "string"
        ) {
          cleanedData.referedFromCompany = "";
        }

        if (onSubmit) {
          console.log("🔥 Calling onSubmit callback...");
          const latestValues = form.getValues
            ? (form.getValues() as Record<string, unknown>)
            : (cleanedData as Record<string, unknown>);
          const mergedLatest = {
            ...latestValues,
            ...livePatchRef.current,
          } as Record<string, unknown>;
          console.log("🔥 onSubmit data:", cleanedData);
          console.log("🔥 onSubmit mergedLatest:", mergedLatest);
          await (onSubmit as any)(cleanedData, mergedLatest);
          console.log("🔥 onSubmit callback completed");
        } else {
          console.log("🔥 No onSubmit callback provided");
        }
      } catch (error) {
        console.error("Form submission error:", error);
      } finally {
        setLoading(false);
      }
    },
    [onSubmit, validationSchema, form, needsDynamicValidation]
  );

  const renderPrivacyHelper = useCallback((text?: string): React.ReactNode => {
    if (!text) return undefined;
    const parts = text.split(/(Privacy\s*Policy)/i);
    if (parts.length === 1) return text;
    return (
      <>
        {parts.map((part, idx) => {
          if (/privacy\s*policy/i.test(part)) {
            return (
              <Link
                key={`pp-${idx}`}
                onClick={() => setPrivacyOpen(true)}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Privacy Policy
              </Link>
            );
          }
          return <span key={`txt-${idx}`}>{part}</span>;
        })}
      </>
    );
  }, []);

  // Reactive CountLabel component that watches form values
  const ReactiveCountLabel = ({ field }: { field: FieldConfig }) => {
    // Watch the specific fields that the expression depends on
    const under16Dependents =
      useWatch({ control: form.control, name: "under16Dependents" }) || 0;
    const between16to18Dependents =
      useWatch({ control: form.control, name: "between16to18Dependents" }) || 0;
    const otherAdultsOver18 =
      useWatch({ control: form.control, name: "otherAdultsOver18" }) || 0;
    const budgetWithPartner = useWatch({
      control: form.control,
      name: "budgetWithPartner",
    });
    const jointAccount = useWatch({
      control: form.control,
      name: "jointAccount",
    });

    const calculateCountValue = () => {
      if (field.valueExpression) {
        try {
          const formValues = {
            under16Dependents: Number(under16Dependents),
            between16to18Dependents: Number(between16to18Dependents),
            otherAdultsOver18: Number(otherAdultsOver18),
            budgetWithPartner: budgetWithPartner,
            jointAccount:
              typeof jointAccount === "string"
                ? jointAccount
                : budgetWithPartner
                  ? "Y"
                  : "N",
          };
          const result = evaluateNode(field.valueExpression, formValues);
          const num = typeof result === "number" ? result : Number(result);
          return Number.isFinite(num) ? num : 0;
        } catch {
          return 0;
        }
      }
      return 0;
    };

    return (
      <CountLabel
        key={field.fieldName}
        label={field.label}
        value={calculateCountValue()}
      />
    );
  };

  const renderField = useCallback(
    (
      field: FieldConfig,
      suppressFieldError: boolean = false,
      sectionName: string = "",
      scopedPrefix?: string
    ) => {
      // Skip hidden fields
      if (field.isHidden) return null;

      // Auto-detect scoped prefix from field name (e.g., '__listform_temp_arrears__.companyName' -> '__listform_temp_arrears__')
      let detectedScopedPrefix = scopedPrefix;
      if (
        !detectedScopedPrefix &&
        field.fieldName.includes("__listform_temp_")
      ) {
        const parts = field.fieldName.split(".");
        if (
          parts.length > 1 &&
          parts[0] &&
          parts[0].startsWith("__listform_temp_")
        ) {
          detectedScopedPrefix = parts[0];
        }
      }

      const error = form.formState.errors[field.fieldName];
      // Normalize error messages (e.g., any variant of invalid date)
      const normalizeErrorMessage = (msg?: string): string | undefined => {
        if (typeof msg !== "string") return undefined;
        const lower = msg.trim().toLowerCase();
        if (
          lower === "invalid date" ||
          lower === "invaliddate" ||
          (lower.includes("invalid") && lower.includes("date"))
        ) {
          return "Please enter a valid date";
        }
        return msg;
      };
      const normalizedErrorMessage = normalizeErrorMessage(
        error?.message as string | undefined
      );
      const fieldValue = form.watch(field.fieldName);
      // IMPORTANT: Merge with livePatchRef to get the most recent user changes for visibility evaluation
      const rawFormValues = form.watch();
      const allFormValues = mergeWithLivePatch(rawFormValues);

      const normalizeFieldString = (value: unknown): string | undefined => {
        if (typeof value !== "string") {
          return undefined;
        }
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
      };

      const fieldUiConfig = (field.ui ?? {}) as Record<string, unknown>;
      const fieldTitle =
        normalizeFieldString(fieldUiConfig.title) ??
        normalizeFieldString((field as unknown as { title?: string }).title) ??
        normalizeFieldString(field.label);
      const fieldDescription = normalizeFieldString(fieldUiConfig.description);
      const containerVariant = (() => {
        const rawVariantValue = fieldUiConfig.containerVariant;
        if (typeof rawVariantValue !== "string") {
          return undefined;
        }
        const normalized = rawVariantValue.trim().toLowerCase();
        if (
          normalized === "paper" ||
          normalized === "outlined" ||
          normalized === "plain"
        ) {
          return normalized as "paper" | "outlined" | "plain";
        }
        return undefined;
      })();
      const showErrorInContainer = (() => {
        const rawFlag = fieldUiConfig.showErrorInContainer;
        if (typeof rawFlag === "boolean") {
          return rawFlag;
        }
        if (typeof rawFlag === "string") {
          const normalized = rawFlag.trim().toLowerCase();
          if (normalized === "true") return true;
          if (normalized === "false") return false;
        }
        return false;
      })();

      // Check if field should be visible based on visibleWhen condition
      // Use enhanced evaluation that supports scoped fields
      let isFieldVisible = computeIsVisible(
        field,
        allFormValues,
        detectedScopedPrefix
      );
      // Fallback: ensure arrears form becomes visible when sibling inArrears is true
      if (
        !isFieldVisible &&
        field.dataType === "arrear-form" &&
        typeof field.fieldName === "string" &&
        field.fieldName.endsWith(".arrears")
      ) {
        try {
          const parts = field.fieldName.split(".");
          const parentPath = parts.slice(0, -1).join(".");
          const inArrearsVal = form.getValues
            ? (form.getValues(`${parentPath}.inArrears`) as boolean | undefined)
            : undefined;
          if (inArrearsVal === true) {
            isFieldVisible = true;
          }
        } catch {
          // ignore fallback errors
        }
      }
      if (!isFieldVisible) return null;

      // Check if field should be disabled based on other field values
      const isFieldDisabled = computeIsDisabled(field, allFormValues);

      // Don't show errors for disabled fields or when section-level error display is enabled
      const shouldShowError =
        !!error && !isFieldDisabled && !suppressFieldError;

      // Build validation rules from field config
      const validationRules: any = {};
      if (field.isRequired) {
        validationRules.required = true;
      }
      if (field.validationRules) {
        field.validationRules.forEach((rule) => {
          switch (rule.type) {
            case "minLength":
              validationRules.minLength = rule.value;
              break;
            case "maxLength":
              validationRules.maxLength = rule.value;
              break;
            case "pattern":
              validationRules.pattern = rule.value;
              validationRules.patternMessage = rule.message;
              if ((rule as any).flags) {
                (validationRules as any).patternFlags = (rule as any).flags;
              }
              break;
            case "minSelections":
              validationRules.minSelections = rule.value;
              break;
            case "maxSelections":
              validationRules.maxSelections = rule.value;
              break;
            case "minAge":
              validationRules.minAge = rule.value;
              break;
            case "maxAge":
              validationRules.maxAge = rule.value;
              break;
          }
        });
      }

      switch (field.dataType) {
        case "separator": {
          const variant = field.ui?.separatorVariant || "solid";
          const thickness = field.ui?.separatorThickness ?? 1;
          const color = field.ui?.separatorColor || theme.palette.divider;
          const spacingY = field.ui?.separatorSpacingY ?? 2;
          return (
            <Box
              key={field.fieldName}
              sx={{
                my: spacingY,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  borderTopWidth: thickness,
                  borderTopStyle: variant === "dashed" ? "dashed" : "solid",
                  borderTopColor: color,
                }}
              />
            </Box>
          );
        }

        case "special-text": {
          const registered = form.register(field.fieldName);
          // Get checkbox field name and state if allowNoValue is enabled
          const noValueFieldName = field.ui?.noValueFieldName as
            | string
            | undefined;
          // Initialize the checkbox field value if it exists and hasn't been initialized yet
          if (
            noValueFieldName &&
            !initializedCheckboxFields.current.has(noValueFieldName)
          ) {
            const currentValue = form.getValues(noValueFieldName);
            if (currentValue === undefined) {
              form.setValue(noValueFieldName, false, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
              });
            }
            initializedCheckboxFields.current.add(noValueFieldName);
          }
          const noValueChecked = noValueFieldName
            ? (form.watch(noValueFieldName) as boolean)
            : false;

          // Don't show errors when "no value" checkbox is checked
          const actualShouldShowError = noValueChecked
            ? false
            : shouldShowError;

          const referenceHelp = resolveReferenceHelp(
            field.fieldName,
            allFormValues,
            field.ui?.referenceHelp
          );
          const helpDialog = referenceHelp.helpDialog ?? field.ui?.helpDialog;
          const helpLinkText =
            referenceHelp.helpLinkText ?? field.ui?.helpLinkText;

          // Build optional props for noValue checkbox feature
          const noValueProps: any = {};
          if (field.ui?.allowNoValue) {
            noValueProps.allowNoValue = true;
            // Use dynamic checkbox label from reference help, then field config, then default
            noValueProps.noValueCheckboxLabel =
              referenceHelp.noValueCheckboxLabel ??
              field.ui.noValueCheckboxLabel ??
              "I don't have this information";
            if (noValueFieldName) {
              noValueProps.noValueFieldName = noValueFieldName;
              noValueProps.noValueChecked = noValueChecked;
              noValueProps.onNoValueChange = (checked: boolean) => {
                form.setValue(noValueFieldName, checked, {
                  shouldValidate: false,
                });
                // Clear the text field and error when checkbox is checked
                if (checked) {
                  form.setValue(field.fieldName, "", { shouldValidate: false });
                  form.clearErrors(field.fieldName);
                } else {
                  // When unchecked, re-validate the field
                  form.trigger(field.fieldName);
                }
              };
            }
          }

          return (
            <SpecialTextField
              key={field.fieldName}
              {...registered}
              onChange={(e: any) => {
                const originalValue = e?.target?.value as string | undefined;
                const isAccount = isAccountField(field.fieldName);
                if (isAccount && typeof originalValue === "string") {
                  const isOverLimit = originalValue.length > 50;
                  const hasApostrophe = originalValue.includes("'");

                  if (isOverLimit || hasApostrophe) {
                    triggerBlockedInputAlert(isOverLimit);
                    const sanitized = originalValue
                      .replace(/'/g, "")
                      .slice(0, 50);
                    const syntheticEvent = {
                      ...e,
                      target: {
                        ...e.target,
                        value: sanitized,
                      },
                    } as any;
                    registered.onChange(syntheticEvent);
                    triggerFieldEvents("onChange", field, sanitized);
                    return;
                  }
                }
                registered.onChange(e);
                triggerFieldEvents("onChange", field, originalValue);
              }}
              type="text"
              label={field.label}
              placeholder={field.ui?.placeholder || field.helpText || ""}
              disabled={isFieldDisabled}
              error={actualShouldShowError}
              helperText={
                (actualShouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              deferValidation
              showCharacterCount={!!field.maxLength}
              showValidationIcon
              inputProps={{
                maxLength:
                  field.maxLength ??
                  (isAccountField(field.fieldName) ? 50 : undefined),
                onKeyDown: (ke: any) => {
                  if (
                    isAccountField(field.fieldName) &&
                    ke?.key === "'"
                  ) {
                    ke.preventDefault();
                    triggerBlockedInputAlert(false);
                  }
                },
                onPaste: (pe: any) => {
                  const maxLength =
                    field.maxLength ??
                    (isAccountField(field.fieldName) ? 50 : undefined);
                  if (maxLength) {
                    const pastedText = pe.clipboardData.getData("text");
                    const selectionStart = pe.target.selectionStart || 0;
                    const selectionEnd = pe.target.selectionEnd || 0;
                    const currentLength = pe.target.value.length;
                    if (
                      currentLength - (selectionEnd - selectionStart) + pastedText.length >
                      maxLength
                    ) {
                      triggerBlockedInputAlert(true);
                    }
                  }
                },
              }}              fullWidth
              helperTop={field.ui?.helperTop}
              helperAttached={field.ui?.helperAttached}
              helperBottom={field.ui?.helperBottom}
              showErrorBelow={field.ui?.showErrorBelow ?? true}
              helpDialog={helpDialog}
              helpLinkText={helpLinkText}
              title={fieldTitle}
              description={fieldDescription}
              containerVariant={containerVariant}
              showErrorInContainer={showErrorInContainer}
              {...noValueProps}
            />
          );
        }

        case "text":
        case "email":
        case "tel":
        case "password":
        case "textarea": {
          // For nested fields (inside question-groups, e.g. "gas.gasSupplierOther"),
          // use RHF Controller (same pattern as text-with-tooltip / amount fields)
          // so the input works reliably when the field becomes dynamically visible.
          const isNestedField = field.fieldName.includes('.');

          if (isNestedField) {
            const nestedInitialValue = fieldValue != null ? String(fieldValue) : '';
            return (
              <NestedTextField
                key={field.fieldName}
                fieldName={field.fieldName}
                initialValue={nestedInitialValue}
                onChange={(val: string) => {
                  form.setValue(field.fieldName, val, { shouldValidate: false, shouldDirty: true });
                  syncNestedFieldState(field.fieldName, val);
                  triggerFieldEvents("onChange", field, val);
                }}
                type={field.dataType === "textarea" ? "text" : field.dataType}
                label={field.label || undefined}
                placeholder={field.ui?.placeholder || field.helpText || ""}
                disabled={isFieldDisabled}
                error={shouldShowError}
                helperText={
                  (shouldShowError
                    ? (normalizedErrorMessage as string | undefined)
                    : undefined) || (field.helpText ? field.helpText : undefined)
                }
                validation={validationRules}
                deferValidation
                showPasswordToggle={field.dataType === "password"}
                showCharacterCount={!!field.maxLength}
                showValidationIcon
                inputProps={{ maxLength: field.maxLength ?? undefined }}
                fullWidth
                helperTop={field.ui?.helperTop}
                helperAttached={field.ui?.helperAttached}
                helperBottom={field.ui?.helperBottom}
                showErrorBelow={field.ui?.showErrorBelow ?? true}
                {...(fieldTitle ? { title: fieldTitle } : {})}
                {...(fieldDescription ? { description: fieldDescription } : {})}
                {...(containerVariant ? { containerVariant } : {})}
                showErrorInContainer={showErrorInContainer}
                multiline={field.dataType === "textarea"}
                rows={
                  field.dataType === "textarea"
                    ? (4 as number | string)
                    : (undefined as unknown as number | string)
                }
                maxRows={
                  field.dataType === "textarea"
                    ? (8 as number | string)
                    : (undefined as unknown as number | string)
                }
              />
            );
          }

          const registered = form.register(field.fieldName);

          return (
            <TextField
              key={field.fieldName}
              {...registered}
              onChange={(e: any) => {
                registered.onChange(e);
                triggerFieldEvents("onChange", field, e?.target?.value);
              }}
              type={field.dataType === "textarea" ? "text" : field.dataType}
              label={field.label || undefined}
              placeholder={field.ui?.placeholder || field.helpText || ""}
              disabled={isFieldDisabled}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText ? field.helpText : undefined)
              }
              validation={validationRules}
              deferValidation
              showPasswordToggle={field.dataType === "password"}
              showCharacterCount={!!field.maxLength}
              showValidationIcon
              inputProps={{ maxLength: field.maxLength ?? undefined }}
              fullWidth
              helperTop={field.ui?.helperTop}
              helperAttached={field.ui?.helperAttached}
              helperBottom={field.ui?.helperBottom}
              showErrorBelow={field.ui?.showErrorBelow ?? true}
              {...(fieldTitle ? { title: fieldTitle } : {})}
              {...(fieldDescription ? { description: fieldDescription } : {})}
              {...(containerVariant ? { containerVariant } : {})}
              showErrorInContainer={showErrorInContainer}
              multiline={field.dataType === "textarea"}
              rows={
                field.dataType === "textarea"
                  ? (4 as number | string)
                  : (undefined as unknown as number | string)
              }
              maxRows={
                field.dataType === "textarea"
                  ? (8 as number | string)
                  : (undefined as unknown as number | string)
              }
            />
          );
        }

        case "select":
        case "multiselect":
          return (
            <Select
              key={field.fieldName}
              label={field.label || ""}
              dialogLabel={
                (processDynamicValue(
                  field.ui?.dialogLabel,
                  allFormValues
                ) as string) || ""
              }
              options={field.options || []}
              value={
                fieldValue !== undefined
                  ? fieldValue
                  : ((field.dataType === "multiselect" ? [] : "") as any)
              }
              onChange={(value) => {
                form.setValue(field.fieldName, value, { shouldValidate: true });
                // Sync nested field state to ensure visibility re-evaluates with latest value
                if (field.fieldName.includes('.')) {
                  syncNestedFieldState(field.fieldName, value);
                }
                triggerFieldEvents("onChange", field, value);
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText ? field.helpText : undefined)
              }
              validation={validationRules}
              multiple={field.dataType === "multiselect"}
              disabled={isFieldDisabled}
              placeholder={field.ui?.placeholder || "Select an option"}
              helperTop={field.ui?.helperTop}
              helperAttached={field.ui?.helperAttached}
              helperBottom={field.ui?.helperBottom}
              {...(field.ui?.helperBottomTone
                ? { helperBottomTone: field.ui.helperBottomTone }
                : {})}
              showErrorBelow={field.ui?.showErrorBelow ?? true}
              {...(fieldTitle ? { title: fieldTitle } : {})}
              {...(fieldDescription ? { description: fieldDescription } : {})}
              {...(containerVariant ? { containerVariant } : {})}
              showErrorInContainer={showErrorInContainer}
              searchable={field.ui?.searchable ?? true}
              allowCustomEntry={Boolean(field.ui?.allowCustomEntry)}
              customEntryLinkText={
                field.ui?.customEntryLinkText ??
                "Can't find my referring company"
              }
              customEntryPlaceholder={
                field.ui?.customEntryPlaceholder ?? "Type your company name"
              }
              customEntryConfirmText={
                field.ui?.customEntryConfirmText ?? "Use this company"
              }
              customEntryCancelText={
                field.ui?.customEntryCancelText ?? "Back to list"
              }
              showValidationIcon
              fullWidth
              deferValidation
              dataPrivate={field.dataPrivate ?? true}
            />
          );

        case "boolean-check":
          return (
            <Box
              key={field.fieldName}
              sx={{
                border: "2px solid",
                borderColor: fieldValue ? "primary.main" : "grey.300",
                borderRadius: "14px",
                padding: "6px 14px",
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                backgroundColor: fieldValue ? "primary.50" : "transparent",
                "&:hover": {
                  borderColor: fieldValue ? "primary.dark" : "grey.400",
                  backgroundColor: fieldValue ? "primary.100" : "grey.50",
                },
                ...(isFieldDisabled && {
                  opacity: 0.6,
                  cursor: "not-allowed",
                }),
                ...(shouldShowError && {
                  borderColor: "error.main",
                  backgroundColor: "error.50",
                }),
              }}
              onClick={async () => {
                if (!isFieldDisabled) {
                  const newValue = !fieldValue;
                  // Set the value first
                  form.setValue(field.fieldName, newValue, {
                    shouldValidate: false,
                  });
                  // Trigger events which may set other fields (guard prevents infinite loops)
                  await triggerFieldEvents("onChange", field, newValue);
                  // Trigger validation after all field updates are complete
                  form.trigger(field.fieldName);
                }
              }}
            >
              <Checkbox
                type="checkbox"
                checked={fieldValue || false}
                onChange={() => {}} // Handled by container click
                disabled={isFieldDisabled}
                icon={<CheckBoxOutlineBlankRoundedIcon />}
                checkedIcon={<CheckBoxRoundedIcon />}
                sx={{
                  padding: 0,
                  marginRight: 1,
                  "& .MuiSvgIcon-root": {
                    fontSize: "18px",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isFieldDisabled
                    ? "text.disabled"
                    : shouldShowError
                      ? "error.main"
                      : "primary.main",
                  fontWeight: 500,
                  userSelect: "none",
                }}
              >
                {field.label}
              </Typography>
              {shouldShowError && (
                <FormHelperText
                  error
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    mt: 0.5,
                  }}
                >
                  {error?.message as string}
                </FormHelperText>
              )}
            </Box>
          );

        case "checkbox": {
          if (field.options && field.options.length > 0) {
            return (
              <CheckboxGroup
                key={field.fieldName}
                label={field.label}
                value={
                  Array.isArray(fieldValue)
                    ? (fieldValue as Array<string | number>)
                    : []
                }
                onChange={(vals: Array<string | number>) => {
                  form.setValue(field.fieldName, vals, {
                    shouldValidate: true,
                  });
                  triggerFieldEvents("onChange", field, vals);
                }}
                options={(field.options || []).map((opt) => ({
                  value: opt.value,
                  title: (opt as any).title || opt.label,
                  description: (opt as any).description,
                }))}
                error={shouldShowError}
                helperText={
                  (shouldShowError ? (error?.message as string) : undefined) ||
                  (field.helpText as string | undefined)
                }
                validation={validationRules}
                disabled={isFieldDisabled}
                variant="outlined"
                title={fieldTitle}
                description={fieldDescription}
                containerVariant={containerVariant}
                showErrorInContainer={showErrorInContainer}
              />
            );
          }

          // Handle confirmation variant with custom styling
          if ((field.ui as any)?.variant === "confirmation") {
            return (
              <Box key={field.fieldName} sx={{ position: "relative" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "12px",
                    gap: "12px",
                    width: "100%",
                    minHeight: "63px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #155F54",
                    borderRadius: "12px",
                    cursor: isFieldDisabled ? "not-allowed" : "pointer",
                    opacity: isFieldDisabled ? 0.6 : 1,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": isFieldDisabled
                      ? {}
                      : {
                          backgroundColor: "#f8faf9",
                          borderColor: "#0f4a45",
                        },
                    ...(shouldShowError && {
                      borderColor: "#d32f2f",
                      backgroundColor: "#fff5f5",
                    }),
                  }}
                  onClick={() => {
                    if (!isFieldDisabled) {
                      const newValue = !fieldValue;
                      form.setValue(field.fieldName, newValue, {
                        shouldValidate: true,
                      });
                      triggerFieldEvents("onChange", field, newValue);
                    }
                  }}
                >
                  <Checkbox
                    type="checkbox"
                    value={fieldValue || false}
                    onChange={() => {}} // Handled by container click
                    disabled={isFieldDisabled}
                    icon={<CheckBoxOutlineBlankRoundedIcon />}
                    checkedIcon={<CheckBoxRoundedIcon />}
                    sx={{
                      padding: 0,
                      color: "#155F54",
                      "&.Mui-checked": {
                        color: "#155F54",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "24px",
                      },
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: isFieldDisabled ? "#9e9e9e" : "#003d40",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      userSelect: "none",
                      flex: 1,
                    }}
                  >
                    {field.label}
                  </Typography>
                </Box>
                {shouldShowError && (
                  <FormHelperText
                    error
                    sx={{
                      mt: 0.5,
                      ml: 1,
                    }}
                  >
                    {normalizedErrorMessage as string}
                  </FormHelperText>
                )}
              </Box>
            );
          }

          return (
            <Checkbox
              key={field.fieldName}
              type="checkbox"
              label={field.label}
              value={fieldValue || false}
              onChange={(checked: boolean) => {
                form.setValue(field.fieldName, checked, {
                  shouldValidate: true,
                });
                triggerFieldEvents("onChange", field, checked);
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
              variant="soft"
            />
          );
        }

        case "radio":
          return (
            <Checkbox
              key={field.fieldName}
              type="radio"
              groupLabel={field.label}
              value={(fieldValue as string) || ""}
              onChange={(val: string) => {
                form.setValue(field.fieldName, val, { shouldValidate: true });
                triggerFieldEvents("onChange", field, val);
              }}
              options={(field.options || []).map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
              variant="outlined"
            />
          );

        case "switch":
          return (
            <Checkbox
              key={field.fieldName}
              type="switch"
              label={field.label}
              value={Boolean(fieldValue)}
              onChange={(checked: boolean) => {
                form.setValue(field.fieldName, checked, {
                  shouldValidate: true,
                });
                triggerFieldEvents("onChange", field, checked);
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
              variant="soft"
            />
          );

        case "toggle-button":
          return (
            <ToggleButton
              key={field.fieldName}
              label={field.label}
              value={fieldValue as boolean | undefined}
              onChange={(value: boolean) => {
                form.setValue(field.fieldName, value, { shouldValidate: true });
                triggerFieldEvents("onChange", field, value);
                // If this is an arrears toggle (ends with '.inArrears') and switched to Yes,
                // emit a sibling "arrearsOpenTick" to force-open the arrears ListForm dialog.
                try {
                  if (
                    value === true &&
                    typeof field.fieldName === "string" &&
                    field.fieldName.endsWith(".inArrears")
                  ) {
                    const parts = field.fieldName.split(".");
                    const parentPath = parts.slice(0, -1).join(".");
                    const tickPath = `${parentPath}.arrearsOpenTick`;
                    (form.setValue as any)(tickPath, Date.now(), {
                      shouldValidate: false,
                      shouldDirty: false,
                      shouldTouch: false,
                    });
                    // Also dispatch a global event to directly instruct the ArrearForm/ListForm to open
                    try {
                      window.dispatchEvent(
                        new CustomEvent("iehub-open-ArrearForm-dialog", {
                          detail: { fieldPath: `${parentPath}.arrears` },
                        })
                      );
                      // Backward compatibility (legacy listeners)
                      window.dispatchEvent(
                        new CustomEvent("iehub-open-listform-dialog", {
                          detail: { fieldPath: `${parentPath}.arrears` },
                        })
                      );
                    } catch {
                      /* ignore */
                    }
                  }
                } catch {
                  // ignore
                }
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
            />
          );

        case "only-label":
          return (
            <Typography
              key={field.fieldName}
              variant="body1"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              {field.label}
            </Typography>
          );

        case "counter":
          return (
            <Counter
              key={field.fieldName}
              label={field.label}
              value={(fieldValue as number) || 0}
              onChange={(value: number) => {
                form.setValue(field.fieldName, value, { shouldValidate: true });
                triggerFieldEvents("onChange", field, value);
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
              min={
                typeof field.ui?.min === "number"
                  ? (field.ui?.min as number)
                  : 0
              }
              max={
                typeof field.ui?.max === "number"
                  ? (field.ui?.max as number)
                  : 99
              }
              disableButtonAtLimit={
                !(
                  typeof field.ui?.limitMessage === "string" &&
                  (field.ui?.limitMessage as string).length > 0
                )
              }
              onLimitExceeded={
                typeof field.ui?.limitMessage === "string" &&
                (field.ui?.limitMessage as string).length > 0
                  ? () => {
                      try {
                        window.dispatchEvent(
                          new CustomEvent("iehub-alert", {
                            detail: {
                              severity: "warning",
                              message: String(field.ui?.limitMessage),
                              variant: "filled",
                              autoHide: true,
                              duration: 2000,
                            },
                          })
                        );
                      } catch {}
                    }
                  : undefined
              }
            />
          );

        case "date-with-clear":
          return (
            <DateWithClear
              key={field.fieldName}
              label={field.label}
              value={(fieldValue as Date) || null}
              onChange={(e: { target: { value: Date | null } }) => {
                form.setValue(field.fieldName, e.target.value, {
                  shouldValidate: form.formState.isSubmitted,
                });
                triggerFieldEvents("onChange", field, e.target.value);
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
              placeholder={field.ui?.placeholder || "MM/DD/YYYY"}
            />
          );

        case "count-label":
          return <ReactiveCountLabel key={field.fieldName} field={field} />;

        case "info-box": {
          const title = field.ui?.title || "";
          const message = field.ui?.message || "";
          const messageHtml = field.ui?.messageHtml || false;
          const variant = field.ui?.variant || "info";

          return (
            <Box
              key={field.fieldName}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                paddingLeft: "20px",
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 0,
                gap: "13px",
                backgroundColor:
                  variant === "info"
                    ? 'transparent'
                    : alpha(theme.palette.warning.main, 0.04),
                borderLeft: "4px solid",
                borderLeftColor:
                  variant === "info"
                    ? '#3B82F6'
                    : theme.palette.warning.main,
                borderRadius: 0,
                minHeight: "fit-content",
                py: 2,
              }}
            >
              {title && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    fontSize: "1rem",
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {title}
                </Typography>
              )}
              {messageHtml ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    fontSize: "0.875rem",
                    margin: 0,
                    "& strong": {
                      fontWeight: 700,
                      color: "text.primary",
                    },
                    "& em": {
                      fontStyle: "italic",
                    },
                    "& ul, & ol": {
                      margin: 0,
                      paddingLeft: "20px",
                    },
                    "& li": {
                      marginBottom: "4px",
                    },
                    "& p": {
                      margin: 0,
                      marginBottom: "8px",
                      "&:last-child": {
                        marginBottom: 0,
                      },
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: message }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    fontSize: "0.875rem",
                    margin: 0,
                  }}
                >
                  {message}
                </Typography>
              )}
            </Box>
          );
        }
        //caresection
        case "question-group": {
          // Derive budget category reliably for warning engine + mapping.
          //
          // Previous implementation inferred from `sectionName` and only covered a
          // subset of budget screens, which caused warning rules (including
          // trigger-based prompts) to silently not run for many sections.
          //
          // The JSON schemas already include a stable `helpTopicId` such as:
          // - budget.income.salary
          // - budget.fixedExpense.transport
          // - budget.flexibleExpense.housekeeping
          // - budget.asset.other-assets
          // - budget.debts.administration
          //
          // We prefer that as the source of truth and keep a sectionName fallback.
          const helpTopicId = String((schema as any)?.helpTopicId ?? "").toLowerCase();
          const derivedCategoryKey = (() => {
            if (helpTopicId.includes("budget.income.salary")) return "salary";
            if (helpTopicId.includes("budget.income.benefits")) return "benefits";
            if (helpTopicId.includes("budget.income.pension")) return "pension";
            // Income other sources uses `budget.income.other-sources` (kebab-case)
            if (helpTopicId.includes("budget.income.other")) return "otherSources";

            if (helpTopicId.includes("budget.asset.properties")) return "properties";
            // Assets other screen uses `budget.asset.other-assets` (kebab-case)
            if (helpTopicId.includes("budget.asset.other")) return "otherAssets";

            if (helpTopicId.includes("budget.fixedexpense.household")) return "household";
            if (helpTopicId.includes("budget.fixedexpense.utilities")) return "utilities";
            if (helpTopicId.includes("budget.fixedexpense.care")) return "care";
            if (helpTopicId.includes("budget.fixedexpense.transport")) return "transport";
            if (helpTopicId.includes("budget.fixedexpense.school")) return "school";
            if (helpTopicId.includes("budget.fixedexpense.pensioninsurance"))
              return "pensionInsurance";
            if (helpTopicId.includes("budget.fixedexpense.professionals"))
              return "professionals";
            if (helpTopicId.includes("budget.fixedexpense.othersources"))
              return "otherSourcesFixed";

            if (helpTopicId.includes("budget.flexibleexpense.leisure")) return "leisure";
            if (helpTopicId.includes("budget.flexibleexpense.housekeeping"))
              return "housekeeping";
            if (helpTopicId.includes("budget.flexibleexpense.personalcosts"))
              return "personalCosts";
            if (helpTopicId.includes("budget.flexibleexpense.savings")) return "savings";

            if (helpTopicId.includes("budget.debts.administration"))
              return "debtsAdministration";
            if (helpTopicId.includes("budget.debts")) return "debts";

            // Fallback: previous heuristic based on sectionName (best-effort)
            const lowerSection = (sectionName || "").toLowerCase();
            if (lowerSection.includes("salary")) return "salary";
            if (lowerSection.includes("benefit")) return "benefits";
            if (lowerSection.includes("pensioninsurance")) return "pensionInsurance";
            if (lowerSection.includes("pension")) return "pension";
            if (lowerSection.includes("other sources") || lowerSection.includes("othersources"))
              return "otherSources";
            if (lowerSection.includes("household")) return "household";
            if (lowerSection.includes("utilities")) return "utilities";
            if (lowerSection.includes("properties")) return "properties";
            if (lowerSection.includes("otherassets")) return "otherAssets";
            if (lowerSection.includes("care")) return "care";
            if (lowerSection.includes("transport")) return "transport";
            if (lowerSection.includes("school")) return "school";
            if (lowerSection.includes("professionals")) return "professionals";
            if (lowerSection.includes("leisure")) return "leisure";
            if (lowerSection.includes("housekeeping")) return "housekeeping";
            if (lowerSection.includes("personalcosts") || lowerSection.includes("personal costs"))
              return "personalCosts";
            if (lowerSection.includes("savings")) return "savings";
            if (lowerSection.includes("debt") && lowerSection.includes("admin"))
              return "debtsAdministration";
            if (lowerSection.includes("debt")) return "debts";
            return undefined;
          })();

          const questionGroupProps: any = {
            label: field.label || "",
            helpText: field.helpText || "",
            showCalculatedIncome: field.showCalculatedIncome ?? false,
            calculatedIncomeTooltip: field.calculatedIncomeTooltip,
            fieldName: field.fieldName,
            categoryKey: derivedCategoryKey,
            form: form,
            sectionName: sectionName,
            onHelpClick: field.helpDialog
              ? () =>
                  setHelpDialogOpen({
                    type: "field",
                    sectionName,
                    fieldName: field.fieldName,
                  })
              : undefined,
            onCalculatedIncomeHelpClick: field.calculatedIncomeHelpDialog
              ? () =>
                  setHelpDialogOpen({
                    type: "field",
                    sectionName,
                    fieldName: `${field.fieldName}_calculatedIncome`,
                  })
              : undefined,
            quickCheck: field.quickCheck,
            onQuickCheckHelpOpen:
              field.quickCheck && field.helpDialog
                ? () =>
                    setHelpDialogOpen({
                      type: "field",
                      sectionName,
                      fieldName: field.fieldName,
                    })
                : undefined,
            onQuickCheckNotesOpen: field.quickCheck
              ? () => {
                  // Find the notes child field
                  const notesField = field.children?.find(
                    (child) => child.fieldName === "notes"
                  );
                  if (notesField) {
                    setNotesDialogOpen({
                      fieldName: field.fieldName,
                      fieldLabel: field.label || "",
                      notesFieldName: `${field.fieldName}.notes`,
                    });
                  }
                }
              : undefined,
            onQuickCheckFieldFocus: field.quickCheck
              ? (targetFieldName: string) => {
                  try {
                    form.setFocus(targetFieldName);
                  } catch (error) {
                    console.warn(
                      `Failed to focus field: ${targetFieldName}`,
                      error
                    );
                  }
                }
              : undefined,
            onQuickCheckOpenSchema: field.quickCheck
              ? (schemaKey: string) => {
                  // Open a dialog that renders another JSON-driven screen with only Save
                  try {
                    const schemaMap: Record<string, any> = {
                      "income-onboarding/1-accomodation-type": require("../../engine/schema/income-onboarding/1-accomodation-type.json"),
                    };
                    const targetSchema = schemaMap[schemaKey];
                    if (targetSchema) {
                      setNotesDialogOpen({
                        fieldName: field.fieldName,
                        fieldLabel: field.label || "",
                        notesFieldName: `${field.fieldName}.notes`,
                      });
                    }
                  } catch (e) {
                    console.warn("Failed to open schema dialog", e);
                  }
                }
              : undefined,
            onOpenFieldEdit: (
              schemaKey: string,
              targetSchemaFieldName: string,
              targetApiFieldName: string
            ) => {
              try {
                // Prefer focusing the field in the current form if available
                if (targetSchemaFieldName) {
                  form.setFocus(targetSchemaFieldName);
                }
              } catch (err) {
                console.warn(
                  "onOpenFieldEdit failed",
                  { schemaKey, targetSchemaFieldName, targetApiFieldName },
                  err
                );
              }
            },
            onWarningAction: (
              action?: OpenActionSpec,
              metadata?: { varId?: string }
            ) => {
              if (!action) return false;

              if (onWarningAction && onWarningAction(action, metadata)) {
                return true;
              }

              const findFieldElement = (fieldPath: string): HTMLElement | null => {
                if (typeof document === "undefined") return null;
                const selectors = [
                  `input[name="${fieldPath}"]`,
                  `textarea[name="${fieldPath}"]`,
                  `select[name="${fieldPath}"]`,
                  `[data-field-path="${fieldPath}"]`,
                ];
                for (const selector of selectors) {
                  const el = document.querySelector<HTMLElement>(selector);
                  if (el) return el;
                }
                return null;
              };

              const highlightElement = (element: HTMLElement) => {
                if (!element) return;
                const highlightRoot =
                  (element.closest('[data-field-root="true"]') as HTMLElement | null) ??
                  element;
                highlightRoot.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "nearest",
                });
                try {
                  if (typeof (element as any).focus === "function") {
                    (element as HTMLInputElement | HTMLTextAreaElement).focus({
                      preventScroll: true,
                    });
                  }
                } catch {
                  // no-op
                }
                highlightRoot.classList.add("warning-highlight");
                window.setTimeout(() => {
                  highlightRoot.classList.remove("warning-highlight");
                }, 2200);
              };

              const focusByPath = (fieldPath: string): boolean => {
                if (!fieldPath) return false;

                // Prefer a DOM-driven focus/highlight event first. Many IE Hub inputs
                // (e.g. `TextWithTooltip`, `CollapsibleTextArea`) aren't registered with
                // react-hook-form and only respond to this event.
                try {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(
                      new CustomEvent("iehub:focus-field", {
                        detail: { fieldPath },
                      })
                    );
                  }
                } catch {
                  // no-op
                }

                // Also attempt RHF focus for standard registered inputs.
                try {
                  form.setFocus(fieldPath);
                } catch {
                  // no-op
                }

                // Defensive fallback: if the input isn't wired to the focus event,
                // try to locate it in the DOM and highlight it (with retries).
                let attempts = 14;
                const attempt = () => {
                  const el = findFieldElement(fieldPath);
                  if (el) {
                    highlightElement(el);
                    return;
                  }
                  attempts -= 1;
                  if (attempts > 0) {
                    window.setTimeout(attempt, 120);
                  }
                };
                window.setTimeout(attempt, 80);

                return true;
              };

              // Notes actions: if the notes field is part of the current question-group,
              // focus/expand it inline (preferred UX). Otherwise, fall back to NotesDialog.
              if (action.focus && action.focus.endsWith(".notes")) {
                const baseFieldName = action.focus.replace(".notes", "");
                const hasNotesChild =
                  typeof field.fieldName === "string" &&
                  baseFieldName === field.fieldName &&
                  Array.isArray(field.children) &&
                  field.children.some((c) => {
                    const n = String((c as any)?.fieldName ?? "");
                    return n === "notes" || n.endsWith(".notes");
                  });

                // Preferred UX: expand/focus the inline notes in this group.
                if (hasNotesChild) {
                  return focusByPath(action.focus);
                }

                // Fallback: try focusing by path (it may exist even if schema children aren't in sync),
                // and only open the NotesDialog if we can't find the field after a short retry window.
                focusByPath(action.focus);
                let attempts = 10;
                const maybeOpen = () => {
                  if (findFieldElement(action.focus as string)) return;
                  attempts -= 1;
                  if (attempts > 0) {
                    window.setTimeout(maybeOpen, 120);
                    return;
                  }
                  const fieldLabel =
                    field.label ||
                    baseFieldName.replace(/([A-Z])/g, " $1").trim();
                  setNotesDialogOpen({
                    fieldName: baseFieldName,
                    fieldLabel: fieldLabel,
                    notesFieldName: action.focus as string,
                  });
                };
                window.setTimeout(maybeOpen, 160);
                return true;
              }

              if (action.focus) {
                return focusByPath(action.focus);
              }

              return false;
            },
            // Pass NI flag for Quick check warning transformations (council tax → council rates)
            isNorthernIreland,
          };

          if (field.tooltipContent) {
            questionGroupProps.mainTooltip = {
              content: field.tooltipContent,
              position: "top" as const,
            };
          }

          // Render children; optionally inject arrears right after a specified child
          const renderedChildren: React.ReactNode[] = [];
          const arrearsInsertAfterChildName = (field.ui as any)
            ?.arrearsInsertAfter as string | undefined;
          let didInjectArrears = false;

          // Pre-compute whether arrears synthetic fields are needed
          const shouldInjectArrears = field.specialArrearsLogic === true;
          // We no longer inject a separate inArrears toggle; ArrearForm manages it internally
          const hasArrearsChild = Array.isArray(field.children)
            ? field.children.some((c) => c.fieldName === "arrears")
            : false;

          // Removed legacy Yes/No toggle injection; ArrearForm renders the checkbox
          const buildSyntheticArrears = (): FieldConfig => {
            // Try to source companies list from the parent supplier select (gas/electricity)
            const supplierChild = (field.children || []).find(
              (c) =>
                c.fieldName === "gasSupplier" ||
                c.fieldName === "electricitySupplier"
            );
            // Try to source frequency options from the parent's frequency child
            const frequencyChild = (field.children || []).find(
              (c) => c.fieldName === "frequency"
            );
            const companiesFromSupplier = Array.isArray(supplierChild?.options)
              ? (supplierChild?.options as any[]).map((opt) => ({
                  value: String(
                    (opt?.value ??
                      opt?.id ??
                      opt?.key ??
                      opt?.code ??
                      opt?.label ??
                      "") ||
                      ""
                  ),
                  label: String(opt?.label ?? opt?.title ?? opt?.value ?? ""),
                }))
              : [];
            const frequencyOptionsFromParent = Array.isArray(
              frequencyChild?.options
            )
              ? (frequencyChild?.options as any[]).map((opt) => ({
                  value: String(opt?.value ?? ""),
                  label: String(opt?.label ?? opt?.title ?? opt?.value ?? ""),
                }))
              : [];
            const parentUi = (field.ui ?? {}) as Record<string, unknown>;
            const parentArrearsCfg =
              parentUi?.arrearsInsertAfter || parentUi?.arrearsConfig
                ? (parentUi?.arrearsConfig as
                    | {
                        formTitle?: string;
                        companies?: Array<{ value: string; label: string }>;
                        products?: Array<{ value: string; label: string }>;
                        accountTypes?: Array<{ value: string; label: string }>;
                        repaymentFrequencyOptions?: Array<{
                          value: string;
                          label: string;
                        }>;
                      }
                    | undefined)
                : undefined;
            // Optional: pick links mapping from the parent question-group
            const arrearsLinks = (field as any)?.linkComponentToArrearsProps as
              | {
                  amount?: string;
                  frequency?: string;
                  notes?: string;
                  companyName?: string;
                  companyOtherName?: string;
                }
              | undefined;
            const mergedCompanies =
              parentArrearsCfg?.companies &&
              parentArrearsCfg.companies.length > 0
                ? parentArrearsCfg.companies
                : companiesFromSupplier;
            const mergedFrequencyOptions =
              parentArrearsCfg?.repaymentFrequencyOptions &&
              parentArrearsCfg.repaymentFrequencyOptions.length > 0
                ? parentArrearsCfg.repaymentFrequencyOptions
                : frequencyOptionsFromParent;

            return {
              fieldName: `${field.fieldName}.arrears`,
              dataType: "arrear-form",
              label: null,
              showAsCheckBox: true,
              addMore: false,
              ui: {
                showArrearsDialog: true,
                arrearsConfig: {
                  formTitle:
                    (parentArrearsCfg?.formTitle as string | undefined) ??
                    "Arrears",
                  companies: mergedCompanies,
                  products: (parentArrearsCfg?.products as any) ?? [],
                  accountTypes: (parentArrearsCfg?.accountTypes as any) ?? [],
                  repaymentFrequencyOptions: mergedFrequencyOptions,
                },
                // Forward links for ArrearForm
                arrearsLinks,
              } as any,
            };
          };

          let uuInjectedForGroup = false;
          const shouldShowDateSolution = (() => {
            if (hideUu) return false;
            try {
              if (!isUuFieldName(field.fieldName)) return false;
              const amountRaw = form.getValues
                ? (form.getValues(`${field.fieldName}.amount`) as unknown)
                : undefined;
              let amountNum = 0;
              if (typeof amountRaw === "number") {
                amountNum = amountRaw;
              } else if (typeof amountRaw === "string") {
                const cleaned = amountRaw
                  .replace(/,/g, "")
                  .replace(/[^\d.-]/g, "");
                const parsed = parseFloat(cleaned);
                if (Number.isFinite(parsed)) amountNum = parsed;
              }
              if (!(amountNum > 0)) return false;
              // Optional referral gate when values are present in the form
              const notReferred = Boolean(
                form.getValues &&
                  (form.getValues("notReferredByCompany") as
                    | boolean
                    | undefined)
              );
              if (notReferred) return false;
              const referredCompany = form.getValues
                ? (form.getValues("referedFromCompany") as string | undefined)
                : undefined;
              if (
                typeof referredCompany === "string" &&
                referredCompany.trim().length > 0
              ) {
                const lower = referredCompany.trim().toLowerCase();
                const isUU = lower.includes("united utilit");
                return isUU;
              }
              // If referral not available in form, check field5TxtFld from customer data
              try {
                const storedCustomer = localStorage.getItem("customerData");
                if (storedCustomer) {
                  const customerObj = JSON.parse(storedCustomer) as Record<
                    string,
                    unknown
                  >;
                  const field5TxtFld =
                    typeof customerObj.field5TxtFld === "string"
                      ? customerObj.field5TxtFld.trim()
                      : "";
                  if (field5TxtFld.length > 0) {
                    return field5TxtFld.toLowerCase().includes("united utilit");
                  }
                }
              } catch {
                // Ignore localStorage/JSON errors
              }
              // No referral info available - don't show UU components
              return false;
            } catch {
              return false;
            }
          })();
          const uuFieldState = uuController.getFieldState(field.fieldName);
          (field.children || []).forEach((childField, idx) => {
            const prefixedFieldName = `${field.fieldName}.${childField.fieldName}`;
            const prefixedChildField = {
              ...childField,
              fieldName: prefixedFieldName,
            };
            const childNode = renderField(
              prefixedChildField,
              false,
              sectionName,
              field.fieldName
            );
            // Insert UU viewer before the notes child in most cases
            // Only show when there's an amount > 0 (same condition as date card)
            if (
              !uuInjectedForGroup &&
              childField.fieldName === "notes" &&
              shouldShowDateSolution
            ) {
              renderedChildren.push(
                <Fragment key={`${field.fieldName}.__uuviewer__`}>
                  <UuViewer
                    fieldName={field.fieldName}
                  label={typeof field.label === "string" ? field.label : null}
                    {...(field.helpDialog
                      ? {
                          onOpenHelp: () =>
                            setHelpDialogOpen({
                              type: "field",
                              sectionName,
                              fieldName: field.fieldName,
                            }),
                        }
                      : {})}
                    {...(uuFieldState?.handleDateChange
                      ? { onDateChange: uuFieldState.handleDateChange }
                      : {})}
                    initialMonth={uuFieldState?.initialMonth ?? ""}
                    initialYear={uuFieldState?.initialYear ?? ""}
                    dateDisabled={uuFieldState?.isSavingDate ?? false}
                    dateError={Boolean(uuFieldState?.dateError)}
                    dateErrorText={uuFieldState?.dateError ?? ""}
                    showDatePicker={shouldShowDateSolution}
                    showUpload={Boolean(uuFieldState?.handleUploadDocument)}
                    uploadedDocument={uuFieldState?.uploadedDocument ?? null}
                    {...(uuFieldState?.handleUploadDocument
                      ? { onDocumentUpload: uuFieldState.handleUploadDocument }
                      : {})}
                    {...(uuFieldState?.handleDeleteDocument
                      ? { onDocumentDelete: uuFieldState.handleDeleteDocument }
                      : {})}
                    {...(uuFieldState?.handleViewDocument
                      ? { onDocumentView: uuFieldState.handleViewDocument }
                      : {})}
                    {...(uuFieldState?.handleDescriptionChange && {
                      onDescriptionChange: uuFieldState.handleDescriptionChange,
                    })}
                    documentUploading={
                      uuFieldState?.isUploadingDocument ?? false
                    }
                  />
                </Fragment>
              );
              uuInjectedForGroup = true;
            }
            renderedChildren.push(
              <Fragment
                key={`${field.fieldName}.${childField.fieldName || idx}`}
              >
                {childNode}
              </Fragment>
            );

            // After rendering the specified child, inject synthetic arrears if requested
            if (
              shouldInjectArrears &&
              !didInjectArrears &&
              arrearsInsertAfterChildName &&
              childField.fieldName === arrearsInsertAfterChildName
            ) {
              // Do not inject the Yes/No toggle; ArrearForm owns the checkbox UI
              if (!hasArrearsChild) {
                const syntheticArrears = buildSyntheticArrears();
                renderedChildren.push(
                  <Fragment key={`${field.fieldName}.arrears::__synthetic__`}>
                    {renderField(
                      syntheticArrears,
                      false,
                      sectionName,
                      field.fieldName
                    )}
                  </Fragment>
                );
              }
              didInjectArrears = true;
            }
          });

          // If an explicit anchor wasn't provided or not found, append as fallback
          if (shouldInjectArrears && !didInjectArrears) {
            // Do not inject the Yes/No toggle; ArrearForm owns the checkbox UI
            if (!hasArrearsChild) {
              const syntheticArrears = buildSyntheticArrears();
              renderedChildren.push(
                <Fragment key={`${field.fieldName}.arrears::__synthetic__`}>
                  {renderField(
                    syntheticArrears,
                    false,
                    sectionName,
                    field.fieldName
                  )}
                </Fragment>
              );
            }
          }

          // If UU viewer not injected (no notes child), add it at the end as a safe fallback
          // Only show when there's an amount > 0 (same condition as date card)
          if (!uuInjectedForGroup && shouldShowDateSolution) {
            renderedChildren.push(
              <Fragment key={`${field.fieldName}.__uuviewer_fallback__`}>
                <UuViewer
                  fieldName={field.fieldName}
                  label={typeof field.label === "string" ? field.label : null}
                    {...(field.helpDialog
                      ? {
                          onOpenHelp: () =>
                            setHelpDialogOpen({
                              type: "field",
                              sectionName,
                              fieldName: field.fieldName,
                            }),
                        }
                      : {})}
                    {...(uuFieldState?.handleDateChange
                      ? { onDateChange: uuFieldState.handleDateChange }
                      : {})}
                  initialMonth={uuFieldState?.initialMonth ?? ""}
                  initialYear={uuFieldState?.initialYear ?? ""}
                  dateDisabled={uuFieldState?.isSavingDate ?? false}
                  dateError={Boolean(uuFieldState?.dateError)}
                  dateErrorText={uuFieldState?.dateError ?? ""}
                  showDatePicker={shouldShowDateSolution}
                  showUpload={Boolean(uuFieldState?.handleUploadDocument)}
                  uploadedDocument={uuFieldState?.uploadedDocument ?? null}
                  {...(uuFieldState?.handleUploadDocument
                    ? { onDocumentUpload: uuFieldState.handleUploadDocument }
                    : {})}
                  {...(uuFieldState?.handleDeleteDocument
                    ? { onDocumentDelete: uuFieldState.handleDeleteDocument }
                    : {})}
                  {...(uuFieldState?.handleViewDocument
                    ? { onDocumentView: uuFieldState.handleViewDocument }
                    : {})}
                  {...(uuFieldState?.handleDescriptionChange && {
                    onDescriptionChange: uuFieldState.handleDescriptionChange,
                  })}
                  documentUploading={uuFieldState?.isUploadingDocument ?? false}
                />
              </Fragment>
            );
          }

          return (
            <QuestionGroup
              key={field.fieldName}
              {...questionGroupProps}
              noPaper={hideFieldCards}
            >
              {renderedChildren}
            </QuestionGroup>
          );
        }

        case "text-with-tooltip": {
          return (
            <Box key={field.fieldName}>
              {field.label && (
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: "16px",
                  }}
                >
                  {field.label}
                </Typography>
              )}
              <Controller
                name={field.fieldName}
                control={(form as any).control}
                defaultValue={fieldValue as any}
                render={({ field: ctl }) => {
                  const currentRaw = String(ctl.value ?? "");
                  const parts = (field.fieldName || "").split(".");
                  const parentKey =
                    parts.length >= 2 ? (parts[parts.length - 2] as string) : "";
                  const isUuAmountField =
                    field.fieldName.endsWith(".amount") && isUuFieldName(parentKey);

                  // Initialize previous value from existing form state so first edit is gated too
                  if (
                    isUuAmountField &&
                    uuAmountPrevByFieldRef.current[parentKey] === undefined
                  ) {
                    uuAmountPrevByFieldRef.current[parentKey] = {
                      raw: currentRaw,
                      num: parseAmountStringToNumber(currentRaw),
                    };
                  }

                  const applyAmountValue = (val: string) => {
                    ctl.onChange(val);
                    syncNestedFieldState(field.fieldName, val);
                    try {
                      const rawValues = (
                        form.getValues ? form.getValues() : {}
                      ) as Record<string, unknown>;
                      const latestValues = mergeWithLivePatch(rawValues);
                      if (typeof onValuesChange === "function") {
                        (onValuesChange as any)(latestValues);
                      }
                    } catch {
                      /* no-op */
                    }
                    triggerFieldEvents("onChange", field, val);
                  };

                  const handleAmountBlur = async (val: string) => {
                    try {
                      ctl.onBlur();
                    } catch {
                      /* no-op */
                    }

                    // --- Inline UU doc guard on onBlur DISABLED ---
                    // Evidence + document cleanup is now handled at save-time
                    // in updateBudgetPage.tsx via uuEvidenceCleanup.ts.
                  };

                  return (
                    <TextWithTooltip
                      name={field.fieldName}
                      value={currentRaw}
                      fieldPath={field.fieldName}
                      onChange={(val: string) => {
                        applyAmountValue(val);

                        // --- Inline UU doc guard on onChange DISABLED ---
                        // Evidence + document cleanup is now handled at save-time
                        // in updateBudgetPage.tsx via uuEvidenceCleanup.ts.
                      }}
                      onBlur={(val: string) => {
                        void handleAmountBlur(val);
                      }}
                      placeholder={field.ui?.placeholder || ""}
                      tooltipVisible={field.tooltipVisible ?? false}
                      tooltipContent={field.tooltipContent}
                    />
                  );
                }}
              />
            </Box>
          );
        }

        case "select-toggle": {
          return (
            <Controller
              key={`ctl-${field.fieldName}`}
              name={field.fieldName}
              control={(form as any).control}
              defaultValue={fieldValue as any}
              render={({ field: ctl }) => (
                <SelectToggle
                  key={`sel-${field.fieldName}`}
                  name={field.fieldName}
                  label={field.label || ""}
                  options={(field.options || []).map((opt) => ({
                    value: String(opt.value),
                    label: opt.label,
                  }))}
                  value={String(ctl.value ?? "")}
                  onChange={(value: string) => {
                    try {
                      /* swallow */
                    } catch {}

                    // 1. Update RHF Controller first
                    ctl.onChange(value);

                    // 2. Manually construct the patch because form.getValues() might be stale immediately
                    try {
                      // Get current committed state
                      const rawValues = (
                        form.getValues ? form.getValues() : {}
                      ) as Record<string, unknown>;
                      const currentValues = mergeWithLivePatch(rawValues);

                      const patch: any = {};
                      const parts = (field.fieldName || "").split(".");

                      // If this is a nested field (e.g. takeHomeSalary.frequency)
                      if (parts.length > 1) {
                        const parentKey = parts[0];
                        // We only support 1 level deep for now
                        if (parts.length === 2 && parentKey) {
                          const childKey = parts[1];
                          if (childKey) {
                            // Get the parent object from current state, or empty
                            const parentObj =
                              (currentValues[parentKey] as Record<
                                string,
                                unknown
                              >) || {};
                            // Create updated parent object with NEW value
                            const nextParent = {
                              ...parentObj,
                              [childKey]: value,
                            };

                            // Set both the nested object AND the flat key in the patch
                            patch[parentKey] = nextParent;
                            patch[field.fieldName] = value;

                            // ALSO force update the RHF state for the parent object to ensure consistency
                            // This helps if other components rely on 'takeHomeSalary' object rather than dot notation
                            if (form.setValue) {
                              (form.setValue as any)(parentKey, nextParent, {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: false, // Don't validate parent object, just leaf
                              });
                            }
                          }
                        }
                      } else {
                        // Simple top-level field
                        patch[field.fieldName] = value;
                      }

                      // 3. Merge patch into current values to get the "next" state
                      // Important: Spread patch LAST to override any stale values in currentValues
                      const latestValues = { ...currentValues, ...patch };

                      // 4. Send to listener (EditFieldDialog)
                      if (typeof onValuesChange === "function") {
                        (onValuesChange as any)(latestValues);
                      }
                    } catch (e) {
                      console.error(
                        "Error in select-toggle onChange patch:",
                        e
                      );
                    }
                    triggerFieldEvents("onChange", field, value);
                  }}
                />
              )}
            />
          );
        }

        case "text-area": {
          return (
            <Controller
              key={`ctl-${field.fieldName}`}
              name={field.fieldName}
              control={(form as any).control}
              defaultValue={fieldValue as string}
              render={({ field: ctl }) => (
                <CollapsibleTextArea
                  label={field.label || ""}
                  value={typeof ctl.value === "string" ? ctl.value : ""}
                  name={ctl.name}
                  onBlur={ctl.onBlur}
                  inputRef={ctl.ref}
                  onKeyDown={(e) => {
                    const val = typeof ctl.value === "string" ? ctl.value : "";
                    if (
                      field.maxLength &&
                      val.length >= field.maxLength &&
                      e.key.length === 1 && // Only check for printable characters
                      !e.ctrlKey &&
                      !e.metaKey
                    ) {
                      showAlert({
                        severity: "warning",
                        message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                      });                    }
                  }}
                  onPaste={(e: any) => {
                    const val = typeof ctl.value === "string" ? ctl.value : "";
                    const pastedText = e.clipboardData.getData("text");
                    const selectionStart = e.target.selectionStart || 0;
                    const selectionEnd = e.target.selectionEnd || 0;
                    if (
                      field.maxLength &&
                      val.length - (selectionEnd - selectionStart) + pastedText.length >
                      field.maxLength
                    ) {
                      showAlert({
                        severity: "warning",
                        message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                      });
                    }
                  }}
                  onChange={(value) => {
                    ctl.onChange(value);
                    syncNestedFieldState(field.fieldName, value);
                    try {
                      const latestValues = (
                        form.getValues ? form.getValues() : {}
                      ) as Record<string, unknown>;
                      if (typeof onValuesChange === "function") {
                        (onValuesChange as any)(latestValues);
                      }
                    } catch {
                      /* no-op */
                    }
                    triggerFieldEvents("onChange", field, value);
                  }}
                  placeholder={field.ui?.placeholder || ""}
                  fieldPath={field.fieldName}
                  {...(typeof field.maxLength === "number"
                    ? { maxLength: field.maxLength }
                    : {})}
                />
              )}
            />
          );
        }

        case "debt-category": {
          const debtContext = (field.ui as any)?.debtContext as
            | {
                lenders?: LenderParentRecord[];
                accountTypes?: AccountTypeRecord[];
                readOnly?: boolean;
                loading?: boolean;
                description?: string;
              }
            | undefined;
          const categoryKey =
            (field.ui?.categoryType as DebtCategoryKey | undefined) ??
            "otherDebts";
          const debtsValue = Array.isArray(fieldValue)
            ? (fieldValue as DebtFormEntry[])
            : [];
          const debtCategoryProps: any = {
            key: field.fieldName,
            label: field.label || "",
            category: categoryKey,
            value: debtsValue,
            onChange: (items: DebtFormEntry[]) => {
              form.setValue(field.fieldName, items, { shouldValidate: true });
              triggerFieldEvents("onChange", field, items);
            },
            placeholder: field.ui?.placeholder || "",
            lenders: debtContext?.lenders ?? [],
            accountTypes: debtContext?.accountTypes ?? [],
            readOnly: isFieldDisabled || Boolean(debtContext?.readOnly),
            loading: Boolean(debtContext?.loading),
            hideAccountType: Boolean((debtContext as any)?.hideAccountType),
            // Provide available budget (numeric) for payment-offer gating in Debts
            availableBudget: (() => {
              try {
                const allValues: Record<string, unknown> =
                  typeof form.getValues === "function"
                    ? (form.getValues() as any)
                    : {};
                const raw = (allValues?.balanceAmount as unknown) ?? undefined;
                if (typeof raw === "number") return raw;
                if (typeof raw === "string") {
                  const cleaned = raw.replace(/,/g, "");
                  const n = parseFloat(cleaned);
                  return Number.isFinite(n) ? n : undefined;
                }
              } catch {
                // ignore parse errors
              }
              return undefined;
            })(),
          };

          const descriptionValue =
            debtContext?.description ?? (field.helpText as string | undefined);
          if (descriptionValue !== undefined) {
            debtCategoryProps.description = descriptionValue;
          }

          return <DebtCategory {...debtCategoryProps} />;
        }

        case "list-form": {
          return (
            <ListForm
              key={field.fieldName}
              label={field.label}
              showAsCheckBox={field.showAsCheckBox ?? false}
              addMore={field.addMore ?? true}
              formTitle={field.formTitle ?? "Add Item"}
              formFields={field.children ?? []}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(items) => {
                form.setValue(field.fieldName, items, { shouldValidate: true });
                triggerFieldEvents("onChange", field, items);
              }}
              error={shouldShowError}
              helperText={
                shouldShowError ? (normalizedErrorMessage as string) : ""
              }
              renderField={(childField) =>
                renderField(childField, false, sectionName)
              }
              form={form}
              fieldName={field.fieldName}
            />
          );
        }
        case "arrear-form": {
          // Support JSON-free arrears dialog via field.ui flags
          const uiConfig = (field.ui ?? {}) as Record<string, unknown>;
          const forceArrearsDialog =
            Boolean(uiConfig.showArrearsDialog) ||
            (typeof field.fieldName === "string" &&
              field.fieldName.endsWith(".arrears"));
          // Build arrearsConfig from ui if provided; fallback will use JSON children options in ListForm
          const arrearsConfig = (() => {
            const cfg = uiConfig.arrearsConfig as
              | {
                  formTitle?: string;
                  companies?: Array<{ value: string; label: string }>;
                  products?: Array<{ value: string; label: string }>;
                  accountTypes?: Array<{ value: string; label: string }>;
                }
              | undefined;
            if (!cfg) return undefined;
            return cfg;
          })();
          // Optional explicit accTypeCode for arrears, provided at JSON level
          const arrearsAccTypeCode =
            typeof uiConfig.arrearsAccTypeCode === "string"
              ? (uiConfig.arrearsAccTypeCode as string)
              : undefined;
          // Optional links mapping forwarded to ArrearForm
          const arrearsLinks = uiConfig.arrearsLinks as
            | {
                amount?: string;
                frequency?: string;
                notes?: string;
                companyName?: string;
                companyOtherName?: string;
              }
            | undefined;
          return (
            <ArrearForm
              key={field.fieldName}
              label={field.label}
              showAsCheckBox={field.showAsCheckBox ?? false}
              addMore={field.addMore ?? true}
              formTitle={field.formTitle ?? "Add Item"}
              formFields={field.children ?? []}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(items) => {
                form.setValue(field.fieldName, items, { shouldValidate: true });
                triggerFieldEvents("onChange", field, items);
              }}
              error={shouldShowError}
              helperText={
                shouldShowError ? (normalizedErrorMessage as string) : ""
              }
              renderField={(childField) =>
                renderField(childField, false, sectionName)
              }
              form={form}
              fieldName={field.fieldName}
              forceArrearsDialog={forceArrearsDialog}
              {...(arrearsConfig ? { arrearsConfig } : {})}
              {...(arrearsLinks
                ? { linkComponentToArrearsProps: arrearsLinks }
                : {})}
              {...(arrearsAccTypeCode ? { arrearsAccTypeCode } : {})}
            />
          );
        }
        case "radiogroup": {
          const helperNode =
            (shouldShowError
              ? (normalizedErrorMessage as string | undefined)
              : undefined) ||
            renderPrivacyHelper(field.helpText as string | undefined);
          return (
            <RadioGroup
              key={field.fieldName}
              label={field.label}
              value={fieldValue || ""}
              onChange={async (value: string | number) => {
                // Set the value first
                form.setValue(field.fieldName, value, {
                  shouldValidate: false,
                });
                // Trigger events which may set other fields (guard prevents infinite loops)
                await triggerFieldEvents("onChange", field, value);
                // Trigger validation after all field updates are complete
                form.trigger(field.fieldName);
              }}
              options={(field.options || []).map((opt) => ({
                // Accept multiple JSON shapes
                value:
                  (opt.value as string | number) ??
                  (opt as any).id ??
                  (opt as any).key ??
                  (opt as any).code ??
                  (opt as any).label ??
                  (opt as any).title ??
                  "",
                title:
                  (opt.title as string | undefined) ??
                  (opt.label as string | undefined) ??
                  String(
                    (opt.value as string | number) ??
                      (opt as any).id ??
                      (opt as any).key ??
                      (opt as any).code ??
                      ""
                  ),
                description:
                  (opt.description as string | undefined) ??
                  (opt as any).helperText ??
                  (opt as any).subtitle ??
                  (opt as any).subTitle,
                disabled: Boolean(
                  (opt as any).disabled ?? (opt as any).isDisabled
                ),
              }))}
              error={shouldShowError}
              helperText={helperNode as React.ReactNode}
              validation={validationRules}
              disabled={isFieldDisabled}
              variant="outlined"
              title={fieldTitle}
              description={fieldDescription}
              containerVariant={containerVariant}
              showErrorInContainer={showErrorInContainer}
            />
          );
        }

        case "date":
        case "time":
        case "datetime":
          return (
            <DateField
              key={field.fieldName}
              type={field.dataType as "date" | "time" | "datetime"}
              label={field.label}
              value={(fieldValue as Date) || null}
              onChange={(e: { target: { value: Date | null } }) => {
                // Only validate on change after the first submit attempt
                form.setValue(field.fieldName, e.target.value, {
                  shouldValidate: form.formState.isSubmitted,
                });
                triggerFieldEvents("onChange", field, e.target.value);
              }}
              error={shouldShowError}
              helperText={
                (shouldShowError
                  ? (normalizedErrorMessage as string | undefined)
                  : undefined) || (field.helpText as string | undefined)
              }
              validation={validationRules}
              disabled={isFieldDisabled}
              fullWidth
              deferValidation={true}
              helperTop={field.ui?.helperTop}
              helperAttached={field.ui?.helperAttached}
              helperBottom={field.ui?.helperBottom}
              showErrorBelow={field.ui?.showErrorBelow ?? true}
              title={fieldTitle}
              description={fieldDescription}
              containerVariant={containerVariant}
              showErrorInContainer={showErrorInContainer}
            />
          );

        default:
          return (
            <Box
              key={field.fieldName}
              sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Unsupported field type: {field.dataType}
              </Typography>
            </Box>
          );
      }
    },
    [
      form,
      computeIsVisible,
      computeIsDisabled,
      evaluateRule,
      navigate,
      parseVisibilityCondition,
      renderPrivacyHelper,
      triggerBlockedInputAlert,
      setHelpDialogOpen,
      setNotesDialogOpen,
      setPrivacyOpen,
      theme,
      triggerFieldEvents,
      processDynamicValue,
      ReactiveCountLabel,
      mergeWithLivePatch,
      syncNestedFieldState,
      hideFieldCards,
      uuController,
      isAccountField,
      onValuesChange,
      onWarningAction,
    ]
  );

  const renderSection = useCallback(
    (section: SectionConfig) => {
      // Get current form values for visibility evaluation
      // IMPORTANT: Merge with livePatchRef to get the most recent user changes
      // This fixes visibility not updating when user types (livePatch has newer values than form.getValues())
      const rawFormValues = form.getValues();
      const currentFormValues = mergeWithLivePatch(rawFormValues);

      // Filter out hidden fields, evaluate visibility conditions, and sort by order
      const visibleFields = section.fields
        .filter((field) => {
          return computeIsVisible(field, currentFormValues);
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      // If no fields are visible, still render the section when helper text or a label is provided
      // so message-only sections (like School when no dependents) can display content.
      const hasHeaderOrHelper = Boolean(
        section.sectionHelperText || section.sectionLabel
      );
      if (visibleFields.length === 0 && !hasHeaderOrHelper) return null;

      // Generate dynamic fields based on counter values
      const generateDynamicFields = () => {
        const dynamicFields: FieldConfig[] = [];

        visibleFields.forEach((field) => {
          if (field.dataType === "date-with-clear") {
            // Check if this is a dependent DOB field that should be repeated
            if (field.fieldName === "under16DependentsDOB") {
              const under16Count = Number(
                currentFormValues.under16Dependents || 0
              );
              for (let i = 1; i <= under16Count; i++) {
                const dynamicField: FieldConfig = {
                  ...field,
                  fieldName: `under16DependentsDOB_${i}`,
                  label: `Dependent Child ${i} Date Of Birth`,
                  order: (field.order || 0) + i * 0.1, // Slight offset to maintain order
                  ui: {
                    ...field.ui,
                    placeholder: `Enter dependent child ${i} date of birth`,
                    helperTop: `Please Enter Your dependent child ${i} date of birth`,
                  },
                };
                dynamicFields.push(dynamicField);
              }
            } else if (field.fieldName === "between16to18DependentsDOB") {
              const between16to18Count = Number(
                currentFormValues.between16to18Dependents || 0
              );
              for (let i = 1; i <= between16to18Count; i++) {
                const dynamicField: FieldConfig = {
                  ...field,
                  fieldName: `between16to18DependentsDOB_${i}`,
                  label: `Dependent Child ${i} Date Of Birth`,
                  order: (field.order || 0) + i * 0.1, // Slight offset to maintain order
                  ui: {
                    ...field.ui,
                    placeholder: `Enter dependent child ${i} date of birth`,
                    helperTop: `Please Enter Your dependent child ${i} date of birth`,
                  },
                };
                dynamicFields.push(dynamicField);
              }
            } else {
              // Regular date field, add as is
              dynamicFields.push(field);
            }
          } else {
            // Non-date fields, add as is
            dynamicFields.push(field);
          }
        });

        return dynamicFields.sort((a, b) => (a.order || 0) - (b.order || 0));
      };

      const fieldsToRender =
        visibleFields.length > 0 ? generateDynamicFields() : [];

      // Check if any field in this section has showErrorOnSection enabled
      const hasShowErrorOnSection = fieldsToRender.some(
        (field) => field.ui?.showErrorOnSection
      );

      // Collect section-level errors if showErrorOnSection is enabled
      const sectionErrors: string[] = [];
      if (hasShowErrorOnSection) {
        fieldsToRender.forEach((field) => {
          if (field.ui?.showErrorOnSection) {
            const error = form.formState.errors[field.fieldName];
            if (error && error.message) {
              sectionErrors.push(error.message as string);
            }
          }
        });
      }

      return (
        <Box
          key={section.sectionName}
        >
          {section.sectionLabel && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: 0,
                gap: 1.5, // 12px gap as specified in CSS
                mb: isMobile ? 2.25 : 2.5,
                // Add red left border for entire section when there are errors
                ...(hasShowErrorOnSection &&
                  sectionErrors.length > 0 && {
                    borderLeft: "4px solid",
                    borderLeftColor: "error.main",
                    paddingLeft: 2,
                    paddingBottom: 0,
                    minHeight: "auto",
                    height: "fit-content",
                    paddingTop: 0,
                    marginBottom: isMobile ? 2.25 : 2.5,
                  }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: (theme.palette as any).brand?.main || "#155F54", // Use brand color with fallback
                    letterSpacing: "-0.02em",
                    mb: 0, // Remove margin since we're using gap
                  }}
                >
                  {section.sectionLabel}
                </Typography>
                {section.sectionTooltipContent && (
                  <Tooltip
                    title={section.sectionTooltipContent}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#9CA3AF",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {section.helpDialog && (
                  <Tooltip title="Help & FAQs" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setHelpDialogOpen({
                          type: "section",
                          sectionName: section.sectionName,
                        })
                      }
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#9CA3AF",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {section.sectionHelperText && (
                <SectionHelperText
                  text={section.sectionHelperText}
                  {...(section.sectionHelperTextHtml !== undefined && {
                    isHtml: section.sectionHelperTextHtml,
                  })}
                  {...(section.sectionHelperTextWordLimit !== undefined && {
                    wordLimit: section.sectionHelperTextWordLimit,
                  })}
                />
              )}

              {/* Section-level error display */}
              {hasShowErrorOnSection && sectionErrors.length > 0 && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 1,
                  }}
                >
                  {sectionErrors.map((errorMessage, index) => {
                    const msg = (() => {
                      const lower = String(errorMessage).trim().toLowerCase();
                      if (
                        lower === "invalid date" ||
                        lower === "invaliddate" ||
                        (lower.includes("invalid") && lower.includes("date"))
                      ) {
                        return "Please enter a valid date";
                      }
                      return errorMessage;
                    })();
                    return (
                      <Typography
                        key={index}
                        variant="body2"
                        color="error"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          mb: 0,
                        }}
                      >
                        {msg}
                      </Typography>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ p: 0 }}>
            <Grid container spacing={isMobile ? 2.25 : 2.5}>
              {fieldsToRender.map((field) => (
                <Grid
                  key={field.fieldName}
                  item
                  xs={12}
                  sm={field.size || 12}
                  md={field.size || 12}
                  lg={field.size || 12}
                >
                  {/* Wrap each field in a white card */}
                  {hideFieldCards ||
                  field.dataType === "question-group" ||
                  field.dataType === "only-label" ||
                  field.dataType === "separator" ||
                  field.dataType === "count-label" ||
                  field.dataType === "info-box" ||
                  field.dataType === "toggle-button" ||
                  field.dataType === "debt-category" ? (
                    // Don't wrap special types in cards or when hideFieldCards is true
                    renderField(
                      field,
                      field.ui?.showErrorOnSection || false,
                      section.sectionName
                    )
                  ) : (
                   <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? 2.25 : 2.5,
                        backgroundColor: "white",
                        border: "1px solid",
                        borderColor: "grey.200",
                        borderRadius: "12px",
                        "&:hover": {
                          borderColor: "grey.300",
                        },
                      }}
                    >
                      {renderField(
                        field,
                        field.ui?.showErrorOnSection || false,
                        section.sectionName
                      )}
                    </Paper>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      );
    },
    [
      renderField,
      form,
      parseVisibilityCondition,
      theme,
      hideFieldCards,
      computeIsVisible,
    ]
  );

  if (!schema) {
    return (
      <Container maxWidth="lg" disableGutters sx={{ py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No schema provided
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters>
      {/* Form Header */}
      {hideHeader === false && (
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 4,
            textAlign: "center",
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.03em",
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            {schema.formName}
          </Typography>
          {schema.collectionName && (
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              {schema.collectionName}
            </Typography>
          )}
        </Paper>
      )}

      <FormProvider {...form}>
        <form
          id={formId}
          onSubmit={async (e) => {
            console.log("📋 Form onSubmit triggered, formId:", formId);
            console.log("📋 Form state before handleSubmit:", {
              isValid: form.formState.isValid,
              isValidating: form.formState.isValidating,
              isSubmitting: form.formState.isSubmitting,
              errors: form.formState.errors,
            });
            console.log("📋 About to run pre-submit guards...");
            e.preventDefault();
            const okToSubmit = await ensureUuDocGuardBeforeSubmit();
            if (!okToSubmit) {
              console.log("📋 Submission aborted by UU doc guard.");
              return;
            }
            console.log("📋 About to call form.handleSubmit...");
            await form.handleSubmit(handleSubmit)();
          }}
        >
          {/* Form Sections */}
          {schema.sections.map((section, idx) => (
            <Fragment
              key={`section-${section.sectionName || section.sectionLabel || idx}`}
            >
              {renderSection(section)}
            </Fragment>
          ))}

          {/* Form Actions */}
          {hideActions === false && (
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: 3,
                backgroundColor: theme.palette.background.default,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={() => form.reset()}
                disabled={loading}
                size="large"
                sx={{
                  minWidth: 120,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                  },
                }}
              >
                Reset Form
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                loading={loading}
                disabled={loading}
                size="large"
                sx={{
                  minWidth: 160,
                  fontWeight: 600,
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                  },
                }}
              >
                {loading ? "Submitting..." : "Submit Form"}
              </Button>
            </Paper>
          )}
        </form>
      </FormProvider>

      {/* Privacy Policy Drawer */}
      <InfoDrawer
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        content={privacyContent}
      />

      {/* JSON Help Dialog */}
      {helpDialogOpen &&
        (() => {
          const section = schema.sections.find(
            (s) => s.sectionName === helpDialogOpen.sectionName
          );
          if (!section) return null;

          let helpConfig:
            | { title?: string; description?: string; faqs: FAQ[] }
            | undefined;

          if (helpDialogOpen.type === "section") {
            helpConfig = section.helpDialog;
          } else if (
            helpDialogOpen.type === "field" &&
            helpDialogOpen.fieldName
          ) {
            // Check if this is a calculated income help request
            const isCalculatedIncomeHelp =
              helpDialogOpen.fieldName.endsWith("_calculatedIncome");
            const actualFieldName = isCalculatedIncomeHelp
              ? helpDialogOpen.fieldName.replace("_calculatedIncome", "")
              : helpDialogOpen.fieldName;

            // Find the field in the section
            const field = section.fields.find(
              (f) => f.fieldName === actualFieldName
            );

            if (isCalculatedIncomeHelp) {
              helpConfig = field?.calculatedIncomeHelpDialog;
            } else {
              helpConfig = field?.helpDialog;
            }
          }

          if (!helpConfig) return null;

          const helpDialogProps: {
            open: boolean;
            onClose: () => void;
            title?: string;
            description?: string;
            faqs: FAQ[];
            zIndex?: number;
          } = {
            open: true,
            onClose: () => setHelpDialogOpen(null),
            title: helpConfig.title || "Help & support",
            faqs: helpConfig.faqs || [],
            zIndex: 1302,
          };

          if (helpConfig.description) {
            helpDialogProps.description = helpConfig.description;
          }

          return <JsonHelpDialog {...helpDialogProps} />;
        })()}

      {/* Notes Dialog */}
      {notesDialogOpen && (
        <NotesDialog
          open={true}
          onClose={() => setNotesDialogOpen(null)}
          onSave={(noteText: string) => {
            form.setValue(notesDialogOpen.notesFieldName, noteText, {
              shouldValidate: true,
            });
          }}
          fieldLabel={notesDialogOpen.fieldLabel}
          initialValue={
            (form.getValues(notesDialogOpen.notesFieldName) as string) || ""
          }
          zIndex={1303}
        />
      )}

      {/* UU Document Preview Dialog */}
      {!hideUu && (
        <DocumentPreviewDialog
          open={uuController.previewDialog.isOpen}
          onClose={uuController.closePreviewDialog}
          documentName={uuController.previewDialog.documentName}
          documentUrl={uuController.previewDialog.documentUrl}
          isLoading={uuController.previewDialog.isLoading}
          error={uuController.previewDialog.error}
          mimeType={uuController.previewDialog.mimeType}
        />
      )}

      {/* Debug info - only in development */}
      {/* {import.meta.env.DEV && (
        <Paper
          elevation={0}
          sx={{ 
            mt: 4, 
            p: 3, 
            backgroundColor: alpha(theme.palette.info.main, 0.04),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}
        >
          <Typography 
            variant="subtitle2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: theme.palette.info.dark,
              mb: 2
            }}
          >
            Debug Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Form Name</Typography>
              <Typography variant="body2" fontWeight={500}>{schema.formName}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Version</Typography>
              <Typography variant="body2" fontWeight={500}>{schema.version}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Total Fields</Typography>
              <Typography variant="body2" fontWeight={500}>
                {schema.sections.reduce((total, section) => total + section.fields.length, 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Sections</Typography>
              <Typography variant="body2" fontWeight={500}>{schema.sections.length}</Typography>
            </Grid>
            {schema._id && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">MongoDB ID</Typography>
                <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                  {schema._id.$oid}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )} */}
    </Container>
  );
}

export default SpecialScreen;
